/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const common = {
  it: {
    // App
    appName: 'SanitàNexus',
    tagline: 'Ecosistema digitale e servizi sanitari territoriali',
    welcomeBadge: 'Telemedicina e clinica digitale',
    // Auth
    login: 'Accedi', register: 'Registrati', email: 'Email', password: 'Password',
    fullName: 'Nome completo', specialty: 'Specialità', bio: 'Biografia',
    websiteUrl: 'Sito web', linkedinUrl: 'Profilo LinkedIn',
    category: 'Categoria', medici: 'Medici', infermieri: 'Infermieri', oss: 'OSS',
    infermiere: 'Infermiere', operatoreSS: 'Operatore Socio Sanitario',
    logout: 'Esci',
    // Nav
    feed: 'Feed', bacheca: 'Bacheca', chat: 'Chat', directory: 'Colleghi',
    profile: 'Profilo', settings: 'Impostazioni', resources: 'Risorse',
    notifications: 'Notifiche', admin: 'Amministrazione',
    paziente: 'Paziente',
    // Feed / Posts
    generale: 'Generale', newPost: 'Nuovo post', writeSomething: 'Scrivi qualcosa...',
    addImages: 'Aggiungi immagini', youtubeLink: 'Link YouTube (opzionale)',
    audience: 'Destinatari', publish: 'Pubblica', cancel: 'Annulla',
    like: 'Mi piace', comment: 'Commenta', comments: 'Commenti',
    addComment: 'Aggiungi un commento...', send: 'Invia',
    shareIdea: 'Condividi la tua idea, tutorial o esperienza',
    loginToPost: 'Accedi per pubblicare',
    audienceAll: 'Tutti', audienceSpecific: 'Solo la categoria',
    // Bacheca types
    initiatives: 'Iniziative', initiative: 'Iniziativa',
    helpsProjects: 'Aiuti & Progetti', help: 'Aiuto', project: 'Progetto',
    jobs: 'Proposte di lavoro', job: 'Lavoro',
    free: 'Gratuita', paid: 'A pagamento', join: 'Partecipa', leave: 'Lascia',
    createInitiative: 'Nuovo annuncio',
    title: 'Titolo', description: 'Descrizione', location: 'Luogo',
    startDate: 'Data inizio', endDate: 'Data fine', price: 'Prezzo',
    isPaid: 'A pagamento', deadline: 'Scadenza', contactInfo: 'Contatti',
    type: 'Tipo',
    // Chat
    groupChat: 'Chat di gruppo', privateChats: 'Chat private',
    typeMessage: 'Scrivi un messaggio...',
    groupChatDesc: 'Confrontati con tutti i tuoi colleghi della stessa categoria',
    // Directory
    search: 'Cerca', searchProfessionals: 'Cerca professionisti...',
    allProfessionals: 'Tutti i professionisti',
    // Profile
    message: 'Messaggio', editProfile: 'Modifica profilo',
    // Settings
    language: 'Lingua', italian: 'Italiano', english: 'Inglese',
    spanish: 'Spagnolo', german: 'Tedesco',
    theme: 'Tema', light: 'Chiaro', dark: 'Scuro', system: 'Sistema',
    // Resources
    createResource: 'Aggiungi risorsa', link: 'Link URL',
    pharmacology: 'Farmacologia', procedure: 'Procedura', guideline: 'Linea guida',
    app: 'App', other: 'Altro', wellness: 'Benessere', protocol_it: 'Protocolli IT',
    national_app: 'App Nazionali',
    open: 'Apri', upvote: 'Utile',
    resourcesDesc: 'Link a farmaci, procedure, linee guida e app utili',
    viewAll: 'Vedi tutti',
    // AI Assistant
    aiPoweredBy: 'Powered by Claude Sonnet 4.5',
    aiDisclaimer: 'Assistente AI a scopo informativo per professionisti sanitari. NON e` un dispositivo medico e NON sostituisce diagnosi/valutazione clinica. Verifica sempre le informazioni su fonti ufficiali (AIFA, ISS, linee guida).',
    aiWelcome: 'Come posso aiutarti?',
    aiWelcomeDesc: 'Chiedimi di farmaci, protocolli, procedure, calcoli clinici, linee guida italiane e aggiornamenti scientifici.',
    aiInputPlaceholder: 'Fai una domanda clinica...', aiThinking: 'Sto pensando...',
    newChat: 'Nuova chat',
    feedbackTitle: 'Feedback & Co-design',
    feedbackSubtitle: 'Proponi miglioramenti. La tua opinione guida lo sviluppo.',
    newFeedback: 'Nuovo feedback', feedbackCategory: 'Categoria',
    fb_feature: 'Funzione', fb_bug: 'Bug', fb_ux: 'UX', fb_other: 'Altro',
    fb_status_open: 'Aperto', fb_status_in_progress: 'In corso',
    fb_status_planned: 'Pianificato', fb_status_done: 'Fatto', fb_status_wont_do: 'Rifiutato',
    adminNotes: 'Note admin',
    privacy: 'Privacy', privacyEmail: 'Chi vede la tua email',
    privacyPhone: 'Chi vede il tuo telefono', privacySpecialty: 'Chi vede la tua specialita',
    privacy_public: 'Tutti', privacy_colleagues: 'Colleghi (stessa categoria)', privacy_private: 'Nessuno',
    phone: 'Telefono',
    wellnessSubtitle: 'Supporto psicologico ed economico ai sanitari in burnout',
    burnoutSelfAssessment: 'Auto-valutazione del burnout',
    burnoutAssessmentDesc: 'Un breve questionario anonimo (basato su MBI) per valutare esaurimento emotivo, depersonalizzazione e realizzazione personale.',
    startAssessment: 'Inizia auto-valutazione',
    answerHonestly: 'Rispondi con onesta` in base a come ti sei sentito nell ultimo mese',
    assessmentDisclaimer: 'Questa auto-valutazione e uno strumento indicativo e non sostituisce una diagnosi professionale.',
    overallRisk: 'Rischio complessivo', retake: 'Rifai il test',
    risk_low: 'Basso rischio', risk_moderate: 'Rischio moderato', risk_high: 'Rischio elevato',
    emotionalExhaustion: 'Esaurimento emotivo',
    depersonalization: 'Depersonalizzazione',
    personalAccomplishment: 'Realizzazione personale',
    recommendation_low: 'Ottimo! Il tuo livello di benessere sembra buono. Continua a prenderti cura di te con pause regolari e supporto sociale.',
    recommendation_moderate: 'Attenzione ad alcuni segnali di stress. Considera pause piu lunghe, tecniche di rilassamento e parlane con un collega di fiducia.',
    recommendation_high: 'I risultati indicano un elevato rischio di burnout. Contatta un professionista (psicologo/psichiatra) o il servizio di supporto del tuo Ordine.',
    supportResources: 'Risorse di supporto', italianProtocols: 'Protocolli italiani',
    supportRequests: 'Richieste di supporto', addSupportRequest: 'Aggiungi richiesta',
    noSupportRequests: 'Nessuna richiesta di supporto al momento',
    scaleNever: 'Mai', scaleFewYear: 'Qualche volta all anno', scaleMonthly: 'Una volta al mese',
    scaleFewMonth: 'Qualche volta al mese', scaleWeekly: 'Una volta a settimana',
    scaleFewWeek: 'Qualche volta a settimana', scaleDaily: 'Ogni giorno',
    q_ee1: 'Mi sento emotivamente sfinito/a dal mio lavoro',
    q_ee2: 'Mi sento esausto/a alla fine della giornata lavorativa',
    q_ee3: 'Mi sento stanco/a quando mi alzo al mattino e devo affrontare un altra giornata di lavoro',
    q_ee4: 'Lavorare tutto il giorno con altre persone e uno sforzo per me',
    q_dp1: 'Sento di trattare alcuni pazienti come se fossero oggetti impersonali',
    q_dp2: 'Sono diventato/a piu insensibile verso le persone da quando faccio questo lavoro',
    q_dp3: 'Temo che questo lavoro mi stia indurendo emotivamente',
    q_pa1: 'Riesco a comprendere facilmente cosa provano i miei pazienti',
    q_pa2: 'Nel mio lavoro affronto i problemi emotivi con calma',
    q_pa3: 'Mi sento pieno/a di energia positiva',
    // Notifications
    markAllRead: 'Segna tutte come lette', noNotifications: 'Nessuna notifica',
    // Moderation
    report: 'Segnala', reportReason: 'Motivo della segnalazione',
    reports: 'Segnalazioni', pending: 'In attesa', resolved: 'Risolte',
    dismiss: 'Ignora', deleteTarget: 'Elimina contenuto', reportedBy: 'Segnalato da',
    // Admin
    adminPanel: 'Pannello amministratore', stats: 'Statistiche',
    totalUsers: 'Utenti totali', totalPosts: 'Post totali',
    totalMessages: 'Messaggi totali', pendingReports: 'Segnalazioni in attesa',
    // General
    welcome: 'Benvenuto', welcomeBack: 'Bentornato',
    alreadyHaveAccount: 'Hai già un account?', dontHaveAccount: 'Non hai un account?',
    loginNow: 'Accedi ora', registerNow: 'Registrati ora',
    noPostsYet: 'Nessun post ancora', beFirstToPost: 'Sii il primo a condividere qualcosa',
    noConversations: 'Nessuna conversazione', noInitiatives: 'Nessun annuncio',
    noResources: 'Nessuna risorsa', noResults: 'Nessun risultato',
    loading: 'Caricamento...', confirm: 'Conferma', delete: 'Elimina', remove: 'Rimuovi',
    loginSuccess: 'Accesso effettuato', registerSuccess: 'Registrazione completata',
    postCreated: 'Post pubblicato', initiativeCreated: 'Annuncio creato',
    resourceCreated: 'Risorsa aggiunta', reportSent: 'Segnalazione inviata',
    errorOccurred: 'Si è verificato un errore',
    invalidCredentials: 'Credenziali non valide', emailInUse: 'Email già in uso',
    selectCategory: 'Seleziona una categoria', professionalCategory: 'Categoria professionale',
    images: 'Immagini',
    startChat: 'Inizia chat', viewProfile: 'Vedi profilo',
    noSpecialty: 'Nessuna specialità', noBio: 'Nessuna biografia',
    minutesAgo: '{{count}} min fa', hoursAgo: '{{count}} h fa',
    daysAgo: '{{count}} g fa', justNow: 'Adesso',
    confirmDelete: 'Sei sicuro di voler eliminare?', allCategories: 'Tutte le categorie',
    filterCategory: 'Filtra per categoria', filterType: 'Filtra per tipo',
    resolveAction: 'Risolvi',
    // Onboarding
    ob_welcome_title: 'Benvenuto in SanitàNexus', ob_welcome_desc: 'La community dedicata a medici, infermieri e OSS. Ti guidiamo in 30 secondi tra le funzioni principali.',
    ob_feed_title: 'Feed', ob_feed_desc: 'Il tuo feed personale con post, tutorial ed esperienze di colleghi della tua categoria e generali.',
    ob_bacheca_title: 'Bacheca', ob_bacheca_desc: 'Iniziative, proposte di lavoro, corsi e progetti. Partecipa o crea il tuo annuncio.',
    ob_ai_title: 'Assistente Clinico AI', ob_ai_desc: 'Domande su farmaci, protocolli e linee guida italiane. Risposte istantanee da Claude Sonnet 4.5.',
    ob_wellness_title: 'Benessere e Burnout', ob_wellness_desc: 'Auto-valutazione anonima (MBI), risorse di supporto psicologico ed economico.',
    ob_resources_title: 'Risorse cliniche', ob_resources_desc: 'Farmacologia, procedure, linee guida, app nazionali (FSE, IO, ANMVI...) e altre risorse verificate.',
    ob_chat_title: 'Chat', ob_chat_desc: 'Chat di gruppo per la tua categoria e messaggi privati 1-1 con qualsiasi collega.',
    ob_directory_title: 'Colleghi', ob_directory_desc: 'Trova e contatta altri professionisti sanitari filtrando per categoria e specialità.',
    ob_next: 'Avanti', ob_prev: 'Indietro', ob_skip: 'Salta', ob_finish: 'Inizia',
    // AI stats
    aiStatsTitle: 'Le tue statistiche AI',
    aiStatMyMessages: 'Domande', aiStatSessions: 'Sessioni',
    aiStatActiveDays: 'Giorni attivi (30gg)', aiStatTotalExchanges: 'Scambi totali',
    aiStatLast30d: 'Attività ultimi 30 giorni',
    aiAdminStatsTitle: 'Statistiche AI globali', aiAdminActiveUsers: 'Utenti attivi',
    aiAdminTopUsers: 'Top utenti per messaggi',
    // Settings sections
    settingsAppearance: 'Aspetto', settingsAppearanceDesc: 'Personalizza tema e lingua',
    settingsAccount: 'Account', settingsAccountDesc: 'Profilo, categoria e sicurezza',
    settingsApp: 'App e installazione', settingsAppDesc: 'Installa SanitàNexus sul dispositivo',
    settingsSupport: 'Supporto e info', settingsSupportDesc: 'Feedback, disclaimer e community',
    installApp: 'Installa app', installAppDesc: 'Installa SanitàNexus come app nativa sul tuo dispositivo per un accesso rapido offline.',
    installAppAvailable: 'Installa ora', installAppInstalled: 'App già installata',
    installAppNotAvailable: 'Non disponibile su questo browser. Su iOS usa Safari > Condividi > "Aggiungi a Home".',
    aboutApp: 'Su SanitàNexus', aboutAppDesc: 'La community italiana per professionisti sanitari.',
    versionLabel: 'Versione', privacyPolicy: 'Privacy policy', termsOfUse: 'Termini di utilizzo',
    sendFeedback: 'Invia feedback', contactSupport: 'Contatta il supporto',
    quickActions: 'Azioni rapide',
    // Push notifications
    pushNotifications: 'Notifiche push', pushDesc: 'Ricevi notifiche in tempo reale su messaggi, commenti, iniziative e novità della tua categoria.',
    pushEnable: 'Attiva notifiche', pushDisable: 'Disattiva',
    pushEnabled: 'Notifiche attivate ✓', pushDisabled: 'Notifiche disattivate',
    pushBlocked: 'Notifiche bloccate dal browser. Sblocca dalle impostazioni del sito.',
    pushNotSupported: 'Il tuo browser non supporta le notifiche push.',
    pushSendTest: 'Invia notifica di prova', pushTestSent: 'Notifica di prova inviata',
    pushTestNoDevice: 'Nessun dispositivo iscritto per questo utente',
    // Bookmarks / Search / Today
    save: 'Salva', unsave: 'Rimuovi', saved: 'Salvato',
    savedAdded: 'Salvato tra i preferiti', savedRemoved: 'Rimosso dai preferiti',
    savedTitle: 'Preferiti', savedSubtitle: 'I tuoi post, iniziative e risorse salvati',
    savedEmptyTitle: 'Nessun elemento salvato', savedEmptySub: 'Tocca l\'icona segnalibro per salvare post, risorse e iniziative.',
    savedEmptyPostsTitle: 'Nessun post salvato', savedEmptyPostsSub: 'Salva post interessanti dal feed per rileggerli dopo.',
    savedEmptyResourcesTitle: 'Nessuna risorsa salvata', savedEmptyResourcesSub: 'Salva app, guide e linee guida dalla pagina Risorse.',
    savedEmptyInitiativesTitle: 'Nessuna iniziativa salvata', savedEmptyInitiativesSub: 'Salva proposte di lavoro e progetti dalla Bacheca.',
    all: 'Tutti', posts: 'Post', colleagues: 'Colleghi',
    searchTitle: 'Cerca', searchPlaceholder: 'Cerca post, risorse, colleghi, iniziative...',
    searchHint: 'Almeno 2 caratteri per iniziare la ricerca',
    searchNoResults: 'Nessun risultato per la tua ricerca',
    todayHighlightsTitle: 'In evidenza oggi', todayHighlightsSub: 'Il meglio della community per te',
    trendingPosts: 'Post di tendenza', latestInitiatives: 'Ultime iniziative',
    topResources: 'Risorse più votate', participants: 'partecipanti',
    // Shifts & reminders
    shiftsTitle: 'Turni e promemoria', shiftsSubtitle: 'Il tuo calendario personale',
    invoicingNav: 'Fatturazione',
    consentsNav: 'Consensi FEA',
    refertiNav: 'Referti cifrati',
    portaleNav: 'Portale cifrato',
    validationNav: 'Anti-frode',
    emailNav: 'Email (SMTP)',
    aiUsageNav: 'Consumo AI',
    direzioneNav: 'Direzione',
    backupNav: 'Backup / DR',
    newShift: 'Nuovo', editShift: 'Modifica',
    shiftTitle: 'Titolo', shiftTitlePlaceholder: 'Es. Turno notte reparto',
    shiftType: 'Tipo',
    shiftTypeShift: 'Turno', shiftTypeReminder: 'Promemoria',
    shiftTypeCourse: 'Corso ECM', shiftTypeEvent: 'Evento',
    startAt: 'Inizio', endAt: 'Fine',
    locationOptional: 'Luogo (opzionale)', locationPlaceholder: 'Es. Ospedale San Raffaele',
    notesOptional: 'Note (opzionale)',
    shiftMissingFields: 'Titolo e inizio sono obbligatori',
    shiftCreated: 'Turno creato', shiftUpdated: 'Turno aggiornato',
    shiftDeleted: 'Turno eliminato',
    shiftsEmptyTitle: 'Nessun turno programmato', shiftsEmptySub: 'Aggiungi turni di lavoro, corsi ECM o promemoria personali.',
    upcoming: 'Prossimi', past: 'Passati',
    // Daily briefing
    dailyBriefingTitle: 'Buongiorno! Il tuo briefing di oggi',
    refresh: 'Aggiorna',
    // Admin analytics
    analyticsTitle: 'Analytics per categoria',
    analyticsSubtitle: 'Distribuzione utenti, post e attività per categoria',
    trend7d: 'Trend post ultimi 7 giorni',
    aiAssistant: 'Assistente AI',
    // Telemedicine (Fase 14)
    telemedNavLabel: 'Telemedicina',
    telemedTitle: 'Telemedicina', telemedSubtitle: 'Prenota consulti online con medici certificati SanitàNexus',
    telemedDoctorAreaTitle: 'La tua area Medico', telemedDoctorAreaDesc: 'Gestisci abbonamento, prezzo visita e disponibilità',
    telemedActive: 'Attivo', telemedInactive: 'Non attivo',
    telemedActivateSubTitle: 'Attiva il tuo abbonamento annuale',
    telemedActivateSubDesc: 'Per ricevere prenotazioni e pagamenti dai pazienti, sottoscrivi l\'abbonamento annuale SanitàNexus (49,90€/anno).',
    telemedActivate49: 'Attiva abbonamento • 49,90€/anno',
    telemedExpiresOn: 'Scade il',
    telemedVisitPrice: 'Prezzo visita (€)',
    telemedPlatformFeeNote: 'Commissione piattaforma: 15% per visita',
    telemedAcceptingBookings: 'Accetti prenotazioni?',
    yes: 'Sì', no: 'No',
    telemedConsultationBio: 'Descrizione consulto (visibile ai pazienti)',
    telemedConsultationBioPh: 'Es. Consulti di cardiologia clinica, aritmologia e prevenzione cardiovascolare. Prima visita completa 30 minuti.',
    telemedProfileSaved: 'Profilo telemedicina aggiornato',
    saving: 'Salvataggio...', processing: 'Elaborazione...',
    genericError: 'Si è verificato un errore, riprova',
    telemedFreeCounterTitle: 'Consulti gratuiti questo mese',
    telemedFreeExhausted: 'Hai esaurito i consulti gratuiti del mese. Le prossime prenotazioni saranno a pagamento.',
    telemedFreeRemaining: '{{count}} consulto gratuito rimanente questo mese',
    telemedFreeRemaining_other: '{{count}} consulti gratuiti rimanenti questo mese',
    telemedDoctorsListTitle: 'Medici disponibili',
    telemedActiveDoctors: 'medici attivi',
    telemedNoDoctorsYet: 'Nessun medico ha ancora attivato l\'abbonamento. I medici possono attivarsi dalla loro area personale.',
    telemedVisit: 'visita', telemedFreeTag: 'Gratis',
    telemedBookFree: 'Prenota gratis', telemedPayAndBook: 'Prenota e paga',
    telemedMyAppointments: 'I miei appuntamenti', telemedNoAppointments: 'Nessun appuntamento in agenda',
    telemedStatus_confirmed: 'Confermato', telemedStatus_awaiting_payment: 'In attesa pagamento',
    telemedStatus_cancelled: 'Annullato',
    telemedBookWithDoctor: 'Prenota con', telemedGenericConsultation: 'Consulto medico',
    telemedThisWillBeFree: 'Questo consulto è incluso nei tuoi 2 gratuiti mensili',
    telemedThisWillBePaid: 'Consulto a pagamento (limite mensile gratuito esaurito)',
    telemedDateTime: 'Data e ora',
    telemedReasonOptional: 'Motivo del consulto (opzionale)',
    telemedReasonPh: 'Es. Controllo pressione, dolore toracico da 2 giorni...',




    telemedSubResult: 'Esito abbonamento', telemedApptResult: 'Esito visita',
    telemedSubActivatedTitle: 'Abbonamento attivato!',
    telemedSubActivatedDesc: 'Il tuo abbonamento annuale è attivo. Ora puoi ricevere prenotazioni e pagamenti dai pazienti.',
    telemedApptPaidTitle: 'Pagamento ricevuto!',
    telemedApptPaidDesc: 'La tua visita è confermata. Riceverai i dettagli via notifica.',
    telemedPaymentFailedTitle: 'Pagamento fallito',
    telemedPaymentFailedDesc: 'La transazione non è andata a buon fine. Riprova o usa un altro metodo di pagamento.',
    telemedPaymentCancelledTitle: 'Pagamento annullato',
    telemedPaymentCancelledDesc: 'Hai annullato l\'operazione. Nessun addebito effettuato.',
    telemedPaymentTimeoutTitle: 'Verifica in corso',
    telemedPaymentTimeoutDesc: 'La verifica sta impiegando più del previsto. Aggiorna la pagina fra qualche minuto o controlla nella lista appuntamenti.',
    telemedPaymentErrorTitle: 'Errore', telemedPaymentErrorDesc: 'Impossibile verificare lo stato del pagamento.',
    telemedPaymentCheckingTitle: 'Verifica pagamento in corso...',
    telemedPaymentCheckingDesc: 'Attendi qualche istante mentre confermiamo con Stripe.',
    telemedBackToDashboard: 'Torna alla telemedicina',
    amount: 'Importo',
    // Welcome / Role selection (Task B)
    welcomeChooseTitle: 'Autenticazione Sicura ed Accesso Riservato',
    welcomeChooseSubtitle: 'Seleziona la tua modalità di autenticazione per accedere in sicurezza allo spazio operativo clinico o al portale cifrato end-to-end dei pazienti. Sessione locale protetta on-premise.',
    welcomeProfessionalTitle: 'Sono un professionista sanitario',
    welcomeProfessionalSubtitle: 'Per medici, infermieri e OSS',
    welcomeProBullet1: 'Feed professionale, gruppi per categoria e chat 1-1',
    welcomeProBullet2: 'Assistente clinico AI, protocolli e linee guida italiane',
    welcomeProBullet3: 'Bacheca lavori, iniziative e formazione ECM',
    welcomeProBullet4: 'Modalità Medico: attiva Telemedicina e ricevi pagamenti',
    welcomeProCta: 'Registrati come professionista',
    welcomePatientTitle: 'Sono un utente / paziente',
    welcomePatientSubtitle: 'Cerchi un consulto o una visita online?',
    welcomePatBullet1: 'Prenota consulti con medici verificati',
    welcomePatBullet2: '2 consulti gratuiti al mese, poi tariffa medico',
    welcomePatBullet3: 'Chat sicura 1-1 con il tuo medico',
    welcomePatBullet4: 'Assistente AI sanitaria per informazioni e orientamento',
    welcomePatCta: 'Registrati come utente/paziente',
    welcomeAlreadyRegistered: 'Hai già un account?',
    welcomeBackChoice: 'Cambia tipo di accesso',
    welcomeBadgeGDPR: 'GDPR compliant',
    welcomeBadgeAI: 'AI clinica avanzata',
    // Patient registration
    patientRegisterTitle: 'Crea il tuo account Utente/Paziente',
    patientRegisterSubtitle: 'Solo per prenotare consulti e visite online',
    patientPasswordHint: 'Almeno 6 caratteri',
    patientDob: 'Data di nascita',
    patientDobOptional: 'Facoltativa — utile al medico per il consulto',
    patientConsentTitle: 'Consenso trattamento dati sanitari (GDPR)',
    patientConsentBody: 'Autorizzo SanitàNexus e i medici che sceglierò a trattare i miei dati personali e sanitari per finalità di prenotazione, consulto e cura, ai sensi del Regolamento UE 2016/679 (GDPR). I dati non saranno condivisi con terzi non coinvolti nel consulto.',
    patientConsentRequired: 'Devi accettare il consenso al trattamento dei dati sanitari per registrarti',
    patientCreateAccount: 'Crea account Utente/Paziente',
    // Patient AI Assistant
    aiPatientNavLabel: 'AI Salute',
    aiPatientTitle: 'Assistente AI Salute',
    aiPatientSubtitle: 'Nexi, la tua guida informativa alla salute',
    aiPatientDisclaimerTitle: 'Importante:',
    aiPatientDisclaimerBody: 'Sono un\'assistente AI informativa, non un medico. Non fornisco diagnosi né prescrizioni. Per problemi personali prenota una visita:',
    aiPatientGotoTelemed: 'Vai a Telemedicina',
    aiPatientEmptyTitle: 'Chiedimi qualcosa sulla tua salute',
    aiPatientEmptyDesc: 'Posso spiegarti sintomi comuni, farmaci da banco, esami, prevenzione, quando rivolgerti a uno specialista.',
    aiPatientPlaceholder: 'Scrivi la tua domanda... (es. cosa fare per un mal di testa persistente)',
    aiPatientSugSymptoms: 'Ho mal di testa da 3 giorni, quando devo preoccuparmi?',
    aiPatientSugMed: 'Posso prendere ibuprofene se ho la gastrite?',
    aiPatientSugPrevention: 'Quali esami di controllo dovrei fare a 40 anni?',
    aiPatientSugSpecialist: 'A quale specialista mi devo rivolgere per problemi di sonno?',
    aiPatientBookHint: 'Per un consulto personale con un medico:',
    aiPatientBookCta: 'Prenota una visita',
    aiNewChat: 'Nuova chat',
    aiNoHistoryYet: 'Nessuna conversazione ancora',
    aiUntitledSession: 'Chat',
    aiMessagesCount: 'messaggi',
    patientHealthAppsNav: 'App Salute',
    healthDataNav: 'Dati IoT',
    myServicesNav: 'Prestazioni',
    proToolkitNav: 'Kit Professionista',
    agenticHealthNav: 'Assistente AI',
    voiceDiaryNav: 'Diario Vocale',
    fseNav: 'FSE Lombardia',
    ecmNav: 'Assistente ECM',
    woundCareNav: 'Wound Care',
    careTeamNav: 'Team di cura',
    patientHealthAppsSubtitle: 'App sanitarie ufficiali italiane, banche dati farmaci e app per il monitoraggio dei parametri vitali.',
    // Premium Paziente
    premiumTitle: 'Passa a Premium',
    premiumActiveTitle: 'Premium attivo',
    premiumActive: 'Attivo',
    premiumActiveDesc: 'Il tuo abbonamento Premium è attivo e si rinnova il {{date}}.',
    premiumPitch: 'A soli €{{price}}/mese sblocca più consulti gratuiti, sconto sulle visite e AI Salute illimitata.',
    premiumBenefit1: '{{count}} consulti gratuiti al mese (vs 2 standard)',
    premiumBenefit2: 'Sconto {{pct}}% su tutte le visite a pagamento',
    premiumBenefit3: 'Assistente AI Salute (Nexi) illimitato',
    premiumBenefit4: 'Cancellabile in qualsiasi momento',
    premiumCTA: 'Attiva Premium • €{{price}}/mese',
    premiumResult: 'Esito Premium',
    premiumActivatedTitle: 'Premium attivato!',
    premiumActivatedDesc: 'Ora hai 5 consulti gratuiti/mese, sconto 10% sulle visite e AI Salute illimitata. Il rinnovo è mensile.',

    // Booking Wizard (Sprint 2)
    bookStep1: 'Data & orario', bookStep2: 'Triage', bookStep3: 'Riepilogo',
    bookPickDay: 'Scegli il giorno', bookPickTime: 'Orario disponibile',
    bookNoSlotsForDay: 'Nessuno slot disponibile in questo giorno',
    bookTryAnotherDay: 'Prova un altro giorno oppure contatta il medico',
    bookUrgency: 'Livello di urgenza',
    urgencyRoutine: 'Ordinaria', urgencyRoutineDesc: 'Controllo o consulto non urgente',
    urgencyUrgent: 'Urgente', urgencyUrgentDesc: 'Sintomi che richiedono attenzione a breve',
    urgencyEmergency: 'Emergenza', urgencyEmergencyDesc: 'Sintomi acuti — priorità massima',
    triageIntro: 'Rispondi brevemente per aiutare il medico a prepararsi al consulto.',
    triageSymptoms: 'Sintomi principali', triageSymptomsPh: 'Es. Mal di testa e nausea da 2 giorni',
    triageDuration: 'Durata', triageDurationPh: 'Es. 3 giorni',
    triageSeverity: 'Intensità', triageAllergies: 'Allergie note', triageAllergiesPh: 'Es. Penicillina, arachidi',
    triageCurrentMeds: 'Farmaci in uso', triageCurrentMedsPh: 'Es. Cardioaspirina 100mg',
    bookSummaryTitle: 'Riepilogo prenotazione', bookSummaryDoctor: 'Medico',
    bookSummaryDateTime: 'Data e ora', bookSummaryUrgency: 'Urgenza', bookSummaryPrice: 'Prezzo',
    bookPaymentNote: 'Sarai reindirizzato a Stripe per completare il pagamento in sicurezza.',
    telemedBookingConfirmed: 'Prenotazione confermata!',
    telemedConfirmFreeBooking: 'Conferma prenotazione', telemedProceedToPayment: 'Procedi al pagamento',
    telemedPickDateTime: 'Seleziona data e orario', back: 'Indietro', next: 'Avanti',
    telemedSearchPlaceholder: 'Cerca medico o specializzazione…',
    telemedFilterBySpec: 'Filtra per specializzazione',
    telemedAllSpecs: 'Tutte',
    telemedNoDoctorsMatch: 'Nessun medico corrisponde ai filtri selezionati',
    bookJoinNow: 'Entra ora', bookInVisitNow: 'Entra in visita',

    // Doctor Command Center (Sprint 3)
    doctorAgendaTitle: 'Agenda', doctorAgendaToday: 'Torna a oggi',
    doctorNoApptsToday: 'Nessun appuntamento in questa giornata',
    doctorAllUrgencies: 'Tutte',
    doctorPanelSelectPatient: 'Seleziona un appuntamento',
    doctorPanelSelectPatientDesc: 'Clicca su un appuntamento nella timeline per vedere il triage e la scheda paziente',
    doctorTriageTitle: 'Triage pre-visita',
    doctorViewFullHistory: 'Vedi storico completo paziente',
    doctorKpiToday: 'Oggi', doctorKpiWeek: 'Prossimi 7 giorni',
    doctorKpiCompleted: 'Completate', doctorKpiVisitPrice: 'Tariffa visita',
    availabilityBtn: 'Disponibilità',
    availabilityTitle: 'Disponibilità settimanale',
    availabilityDesc: 'Definisci i giorni e le fasce orarie in cui accetti prenotazioni',
    availabilitySlotMin: 'Durata slot (min)', availabilityBufferMin: 'Pausa tra visite (min)',
    availabilityDays: 'Giorni & orari', availabilityClosed: 'Chiuso',
    availabilitySaved: 'Disponibilità aggiornata',

    // Video Room (Sprint 4)
    videoLive: 'In diretta', videoConnected: 'Connesso',
    videoWaitingDoctor: 'In attesa che il medico avvii la visita…',
    videoWaitingDoctorLong: 'Attendi che il medico avvii la visita. Ti connetteremo automaticamente.',
    videoYou: 'Tu', videoE2ENotice: 'Sessione crittografata',
    videoMockNotice: 'Anteprima MVP — la connessione video reale sarà attivata a breve',
    visitStart: 'Avvia visita', visitStarted: 'Visita avviata',
    visitEnded: 'Visita conclusa',
    notesTitle: 'Note cliniche', notesClinical: 'Note della visita',
    notesClinicalPh: 'Sintesi anamnesi, osservazioni, esami obiettivi…',
    notesDiagnosis: 'Diagnosi (opzionale)', notesDiagnosisPh: 'Es. Cefalea tensiva',
    notesPrescriptions: 'Prescrizioni (opzionale)', notesPrescriptionsPh: 'Es. Paracetamolo 1g x 3/die',
    notesSave: 'Salva nota', noteSaved: 'Nota clinica salvata',
    notesHistory: 'Note precedenti',
    // Waiting room
    waitTitle: 'Sala d\'attesa virtuale',
    waitScheduled: 'Appuntamento previsto: {{time}}',
    waitCountdown: 'Manca',
    waitStartingNow: 'Stiamo per iniziare',
    waitCheckAgain: 'Aggiorna stato',
    waitSecurityNote: 'Sessione protetta end-to-end — nessuna registrazione conservata.',
    waitTip1: 'Trova un ambiente silenzioso e con buona illuminazione per la videochiamata.',
    waitTip2: 'Se possibile, tieni a portata di mano i tuoi esami recenti (referti, esami del sangue).',
    waitTip3: 'Prepara una lista dei farmaci che stai assumendo, incluse dosi e frequenza.',
    waitTip4: 'Bevi acqua e cerca di rimanere calmo — il medico è pronto ad aiutarti.',
    waitTip5: 'Ricorda: in caso di emergenza chiama sempre il 112 (o 118 per emergenze mediche).',

    // Sprint A - Trust & Security
    menuSecurity: 'Sicurezza & Privacy',
    menuWallet: 'Documenti (Wallet)',
    securityBadge: 'Sicurezza & Privacy',
    securityTitle: 'Il tuo centro sicurezza',
    securitySubtitle: 'Gestisci verifica professionale, autenticazione a due fattori, consensi GDPR e cronologia accessi.',
    securityChipAlbo: 'Verifica Albo',
    securityChip2FA: 'Autenticazione 2FA',
    securityChipGDPR: 'Consensi GDPR',
    securityChipAudit: 'Audit log',

    // Albo verify
    alboTitle: 'Verifica iscrizione Albo',
    alboSubtitle: 'Rendi visibile ai pazienti che sei un professionista verificato.',
    alboProfession: 'Professione',
    alboNumber: 'Numero Albo',
    alboProvince: 'Provincia',
    alboVerified: 'Verificato',
    alboPending: 'In attesa',
    alboNotSet: 'Non impostato',
    alboVerifyNow: 'Verifica ora',
    alboReverify: 'Aggiorna verifica',
    alboVerifiedInfo: 'Iscritto Albo n° {{num}} — provincia {{prov}} (verifica automatica SanitàNexus).',
    alboVerifiedSuccess: 'Verifica completata! Ora appari come professionista verificato.',
    alboVerifiedFailed: 'Verifica non riuscita',

    // 2FA
    twoFATitle: 'Autenticazione a due fattori',
    twoFASubtitle: 'Aggiungi un codice temporaneo dalla tua app di autenticazione (Google Authenticator, Authy, 1Password).',
    twoFAOn: 'Attiva',
    twoFAOff: 'Non attiva',
    twoFAStart: 'Attiva 2FA',
    twoFAWhyTitle: 'Perché attivare?',
    twoFAWhyDesc: 'Protegge il tuo account anche se qualcuno conosce la tua password. Obbligatorio per medici che accedono a dati clinici.',
    twoFAScanTitle: 'Scansiona con la tua app',
    twoFAScanDesc: 'Oppure copia manualmente la chiave segreta qui sotto. Inserisci poi il codice a 6 cifre generato.',
    twoFAEnterCode: 'Codice a 6 cifre',
    twoFAConfirm: 'Conferma e attiva',
    twoFAEnabledSuccess: 'Autenticazione a due fattori attivata!',
    twoFADisabledSuccess: '2FA disattivata',
    twoFAActiveTitle: '2FA protegge il tuo account',
    twoFAActiveDesc: 'Al prossimo login ti chiederemo il codice generato dalla tua app di autenticazione.',
    twoFADisable: 'Disattiva 2FA',
    twoFADisableCode: 'Conferma con codice 2FA per disattivare',
    twoFASecretCopied: 'Chiave copiata',
    login2FATitle: 'Verifica 2FA',
    login2FADesc: 'Inserisci il codice di 6 cifre generato dalla tua app di autenticazione.',
    login2FACode: 'Codice',
    login2FAVerify: 'Verifica',
    login2FAPrompt: 'Inserisci il codice 2FA per completare l\'accesso',
    login2FAWrong: 'Codice 2FA non valido',

    // Consents
    consentTitle: 'Consensi Privacy (GDPR)',
    consentSubtitle: 'Controllo granulare su come usiamo i tuoi dati. Puoi revocare i consensi in qualsiasi momento.',
    consentRequired: 'Obbligatorio',
    consentSaved: 'Consensi aggiornati',
    consentNoChanges: 'Nessuna modifica da salvare',
    consentLastUpdated: 'Ultima modifica: {{date}}',

    // Audit
    auditTitle: 'Cronologia accessi',
    auditSubtitle: 'Ogni azione sensibile sul tuo account viene registrata in modo immutabile.',
    auditEmpty: 'Nessuna attività registrata ancora',
    auditAction_albo_verify: 'Verifica albo',
    'auditAction_2fa.enabled': '2FA attivata',
    'auditAction_2fa.setup_started': 'Avvio configurazione 2FA',
    'auditAction_2fa.disabled': '2FA disattivata',
    'auditAction_consent.update': 'Aggiornamento consensi',
    'auditAction_prescription.create': 'Prescrizione emessa',
    'auditAction_prescription.view': 'Prescrizione visualizzata',
    'auditAction_document.upload': 'Documento caricato',
    'auditAction_document.delete': 'Documento eliminato',
    'auditAction_albo.verify': 'Verifica Albo',

    // Wallet
    walletBadge: 'Wallet Digitale',
    walletTitleHero: 'I tuoi documenti sanitari',
    walletSubtitleHero: 'Prescrizioni, referti, esami e certificati sempre con te — proprio come Apple Wallet.',
    walletAdd: 'Aggiungi documento',
    walletPrescriptionsTitle: 'Prescrizioni',
    walletDocumentsTitle: 'Documenti clinici',
    walletNoPrescriptions: 'Nessuna prescrizione ancora. Le prescrizioni emesse dai medici in visita appariranno qui automaticamente.',
    walletNoDocuments: 'Nessun documento salvato. Carica referti, esami o certificati dal tuo cellulare.',
    walletUploadTitle: 'Carica documento',
    walletUploadDesc: 'Aggiungi un referto, un esame o un certificato al tuo wallet.',
    walletUploadSuccess: 'Documento caricato',
    walletTitle: 'Titolo',
    walletTitlePh: 'Es. Emocromo completo',
    walletDocType: 'Tipo documento',
    walletDescription: 'Descrizione (opzionale)',
    walletDescriptionPh: 'Note aggiuntive…',
    walletFile: 'File',
    walletDropOrClick: 'Clicca per caricare un file',
    walletFileHint: 'PDF o immagini, max 4MB',
    walletUpload: 'Carica',
    walletDownload: 'Scarica',
    walletConfirmDelete: 'Vuoi davvero eliminare questo documento?',
    walletDeleted: 'Documento eliminato',

    // Prescriptions
    rxTitle: 'Nuova prescrizione elettronica',
    rxSubtitle: 'Genera una prescrizione con QR di autenticazione, subito disponibile nel wallet del paziente.',
    rxDiagnosis: 'Diagnosi (opzionale)',
    rxDiagnosisPh: 'Es. Cefalea tensiva',
    rxDrugs: 'Farmaci',
    rxDrugName: 'Farmaco *',
    rxDrugDosage: 'Dosaggio (es. 1g)',
    rxDrugFrequency: 'Frequenza (es. 3v/die)',
    rxDrugDuration: 'Durata (es. 5 giorni)',
    rxDrugNotes: 'Note specifiche per questo farmaco',
    rxAddDrug: 'Aggiungi farmaco',
    rxNotes: 'Note generali',
    rxNotesPh: 'Es. Bere abbondante acqua, controllo tra 7 giorni',
    rxCreate: 'Emetti prescrizione',
    rxCreated: 'Prescrizione emessa!',
    rxCreatedTitle: 'Prescrizione emessa',
    rxCreatedDesc: 'Il paziente la vedrà automaticamente nel proprio wallet. Puoi anche scaricare il PDF.',
    rxCodeInfo: 'Il QR code permette la verifica dell\'autenticità del documento.',
    rxDownloadPdf: 'Scarica PDF',
    rxCreateAnother: 'Nuova prescrizione',

    // Password reset flow
    pwResetForgotLink: 'Password dimenticata?',
    pwResetTitle: 'Recupera password',
    pwResetSubtitle: 'Inserisci l\'email del tuo account. Ti invieremo un link per reimpostare la password.',
    pwResetSendLink: 'Invia link di recupero',
    pwResetBackToLogin: 'Torna al login',
    pwResetEmailSent: 'Controlla la tua email',
    pwResetEmailSentDesc: 'Se l\'indirizzo è registrato, riceverai un\'email con un link per reimpostare la password. Il link scade tra 15 minuti.',
    pwResetTryAnother: 'Usa un\'altra email',
    pwResetDevTitle: 'Modalità sviluppatore',
    pwResetDevDesc: 'L\'invio email non è configurato in questo ambiente. Puoi usare il link qui sotto per testare il flusso.',
    pwResetDevCopied: 'Link copiato negli appunti',
    pwResetInvalidTitle: 'Link non valido',
    pwResetInvalidLink: 'Il link è scaduto o non è valido.',
    pwResetRequestNew: 'Richiedi nuovo link',
    pwResetNewTitle: 'Imposta nuova password',
    pwResetNewFor: 'Stai reimpostando la password per {{email}}',
    pwResetNewPassword: 'Nuova password (min. 8 caratteri)',
    pwResetConfirm: 'Conferma password',
    pwResetMismatch: 'Le password non coincidono',
    pwResetSetPassword: 'Aggiorna password',
    pwResetSuccess: 'Password aggiornata! Reindirizzo al login...',
    pwResetDoneTitle: 'Password aggiornata',
    pwResetDoneDesc: 'La tua password è stata cambiata con successo. Ti stiamo reindirizzando al login.',
    pwResetSecurityNote: 'Per la tua sicurezza, tutte le sessioni attive verranno mantenute finché non effettui il login con la nuova password.',
    pwStrengthWeak: 'Debole', pwStrengthMedium: 'Media', pwStrengthStrong: 'Forte',
  },
  en: {
    appName: 'SanitàNexus', tagline: 'Digital ecosystem and territorial healthcare services',
    welcomeBadge: 'Telemedicine and digital clinic',
    login: 'Log in', register: 'Sign up', email: 'Email', password: 'Password',
    fullName: 'Full name', specialty: 'Specialty', bio: 'Bio',
    websiteUrl: 'Website', linkedinUrl: 'LinkedIn profile',
    category: 'Category', medici: 'Doctors', infermieri: 'Nurses', oss: 'Healthcare Workers',
    medico: 'Doctor', infermiere: 'Nurse', operatoreSS: 'Healthcare Worker',
    logout: 'Log out',
    feed: 'Feed', bacheca: 'Board', chat: 'Chat', directory: 'Colleagues',
    profile: 'Profile', settings: 'Settings', resources: 'Resources',
    notifications: 'Notifications', admin: 'Admin',
    paziente: 'User/Patient',
    generale: 'General', newPost: 'New post', writeSomething: 'Write something...',
    addImages: 'Add images', youtubeLink: 'YouTube link (optional)',
    audience: 'Audience', publish: 'Publish', cancel: 'Cancel',
    like: 'Like', comment: 'Comment', comments: 'Comments',
    addComment: 'Add a comment...', send: 'Send',
    shareIdea: 'Share your idea, tutorial or experience',
    loginToPost: 'Log in to post',
    audienceAll: 'Everyone', audienceSpecific: 'My category only',
    initiatives: 'Initiatives', initiative: 'Initiative',
    helpsProjects: 'Help & Projects', help: 'Help', project: 'Project',
    jobs: 'Jobs', job: 'Job',
    free: 'Free', paid: 'Paid', join: 'Join', leave: 'Leave',
    createInitiative: 'New listing',
    title: 'Title', description: 'Description', location: 'Location',
    startDate: 'Start date', endDate: 'End date', price: 'Price',
    isPaid: 'Paid', deadline: 'Deadline', contactInfo: 'Contact', type: 'Type',
    groupChat: 'Group chat', privateChats: 'Private chats', typeMessage: 'Type a message...',
    groupChatDesc: 'Connect with all your colleagues in the same category',
    search: 'Search', searchProfessionals: 'Search professionals...',
    allProfessionals: 'All professionals',
    message: 'Message', editProfile: 'Edit profile',
    language: 'Language', italian: 'Italian', english: 'English',
    spanish: 'Spanish', german: 'German',
    theme: 'Theme', light: 'Light', dark: 'Dark', system: 'System',
    createResource: 'Add resource', link: 'URL link',
    pharmacology: 'Pharmacology', procedure: 'Procedure', guideline: 'Guideline',
    app: 'App', other: 'Other', wellness: 'Wellness', protocol_it: 'IT Protocols',
    national_app: 'National Apps',
    open: 'Open', upvote: 'Useful',
    resourcesDesc: 'Links to drugs, procedures, guidelines & useful apps',
    viewAll: 'View all',
    aiPoweredBy: 'Powered by Claude Sonnet 4.5',
    aiDisclaimer: 'AI assistant for informational purposes for healthcare professionals. NOT a medical device and does NOT replace diagnosis/clinical judgment. Always verify on official sources (AIFA, ISS, guidelines).',
    aiWelcome: 'How can I help you?',
    aiWelcomeDesc: 'Ask me about drugs, protocols, procedures, clinical calculations, Italian guidelines and scientific updates.',
    aiInputPlaceholder: 'Ask a clinical question...', aiThinking: 'Thinking...',
    newChat: 'New chat',
    feedbackTitle: 'Feedback & Co-design',
    feedbackSubtitle: 'Suggest improvements. Your input guides development.',
    newFeedback: 'New feedback', feedbackCategory: 'Category',
    fb_feature: 'Feature', fb_bug: 'Bug', fb_ux: 'UX', fb_other: 'Other',
    fb_status_open: 'Open', fb_status_in_progress: 'In progress',
    fb_status_planned: 'Planned', fb_status_done: 'Done', fb_status_wont_do: 'Won\u2019t do',
    adminNotes: 'Admin notes',
    privacy: 'Privacy', privacyEmail: 'Who sees your email',
    privacyPhone: 'Who sees your phone', privacySpecialty: 'Who sees your specialty',
    privacy_public: 'Everyone', privacy_colleagues: 'Colleagues (same category)', privacy_private: 'No one',
    phone: 'Phone',
    wellnessSubtitle: 'Psychological & financial support for healthcare workers in burnout',
    burnoutSelfAssessment: 'Burnout self-assessment',
    burnoutAssessmentDesc: 'A brief anonymous questionnaire (MBI-based) to gauge emotional exhaustion, depersonalization and personal accomplishment.',
    startAssessment: 'Start assessment',
    answerHonestly: 'Answer honestly based on how you felt in the last month',
    assessmentDisclaimer: 'This self-assessment is indicative and does not replace a professional diagnosis.',
    overallRisk: 'Overall risk', retake: 'Retake',
    risk_low: 'Low risk', risk_moderate: 'Moderate risk', risk_high: 'High risk',
    emotionalExhaustion: 'Emotional exhaustion',
    depersonalization: 'Depersonalization',
    personalAccomplishment: 'Personal accomplishment',
    recommendation_low: 'Great! Your wellbeing seems good. Keep taking care of yourself with regular breaks and social support.',
    recommendation_moderate: 'Watch for some stress signals. Consider longer breaks, relaxation techniques, and talk to a trusted colleague.',
    recommendation_high: 'Results indicate high burnout risk. Please contact a professional (psychologist/psychiatrist) or your association support service.',
    supportResources: 'Support resources', italianProtocols: 'Italian protocols',
    supportRequests: 'Support requests', addSupportRequest: 'Add request',
    noSupportRequests: 'No support requests at the moment',
    scaleNever: 'Never', scaleFewYear: 'A few times a year', scaleMonthly: 'Once a month',
    scaleFewMonth: 'A few times a month', scaleWeekly: 'Once a week',
    scaleFewWeek: 'A few times a week', scaleDaily: 'Every day',
    q_ee1: 'I feel emotionally drained by my work',
    q_ee2: 'I feel exhausted at the end of the workday',
    q_ee3: 'I feel tired when I get up in the morning to face another day of work',
    q_ee4: 'Working with people all day is a strain for me',
    q_dp1: 'I feel I treat some patients/users as if they were impersonal objects',
    q_dp2: 'I have become more callous toward people since I started this work',
    q_dp3: 'I fear this work is hardening me emotionally',
    q_pa1: 'I can easily understand how my patients/users feel',
    q_pa2: 'I deal with emotional problems calmly in my work',
    q_pa3: 'I feel full of positive energy',
    markAllRead: 'Mark all as read', noNotifications: 'No notifications',
    report: 'Report', reportReason: 'Report reason',
    reports: 'Reports', pending: 'Pending', resolved: 'Resolved',
    dismiss: 'Dismiss', deleteTarget: 'Delete content', reportedBy: 'Reported by',
    adminPanel: 'Admin panel', stats: 'Statistics',
    totalUsers: 'Total users', totalPosts: 'Total posts',
    totalMessages: 'Total messages', pendingReports: 'Pending reports',
    welcome: 'Welcome', welcomeBack: 'Welcome back',
    alreadyHaveAccount: 'Already have an account?', dontHaveAccount: "Don't have an account?",
    loginNow: 'Log in', registerNow: 'Sign up',
    noPostsYet: 'No posts yet', beFirstToPost: 'Be the first to share',
    noConversations: 'No conversations', noInitiatives: 'No listings',
    noResources: 'No resources', noResults: 'No results',
    loading: 'Loading...', confirm: 'Confirm', delete: 'Delete', remove: 'Remove',
    loginSuccess: 'Logged in', registerSuccess: 'Registered successfully',
    postCreated: 'Post published', initiativeCreated: 'Listing created',
    resourceCreated: 'Resource added', reportSent: 'Report submitted',
    errorOccurred: 'An error occurred',
    invalidCredentials: 'Invalid credentials', emailInUse: 'Email already in use',
    selectCategory: 'Select a category', professionalCategory: 'Professional category',
    images: 'Images', startChat: 'Start chat', viewProfile: 'View profile',
    noSpecialty: 'No specialty', noBio: 'No bio',
    minutesAgo: '{{count}} min ago', hoursAgo: '{{count}} h ago',
    daysAgo: '{{count}} d ago', justNow: 'Just now',
    confirmDelete: 'Are you sure you want to delete?',
    allCategories: 'All categories', filterCategory: 'Filter by category',
    filterType: 'Filter by type', resolveAction: 'Resolve',
    // Onboarding
    ob_welcome_title: 'Welcome to SanitàNexus', ob_welcome_desc: 'The community for doctors, nurses and healthcare workers. Let\u2019s take a 30-second tour.',
    ob_feed_title: 'Feed', ob_feed_desc: 'Your personal feed with posts, tutorials and experiences from colleagues in your category and general.',
    ob_bacheca_title: 'Board', ob_bacheca_desc: 'Initiatives, job offers, courses and projects. Join or create your own listing.',
    ob_ai_title: 'Clinical AI Assistant', ob_ai_desc: 'Ask about drugs, protocols and Italian guidelines. Instant answers powered by Claude Sonnet 4.5.',
    ob_wellness_title: 'Wellness & Burnout', ob_wellness_desc: 'Anonymous MBI-based self-assessment, psychological and financial support resources.',
    ob_resources_title: 'Clinical resources', ob_resources_desc: 'Pharmacology, procedures, guidelines, national apps (FSE, IO...) and other verified resources.',
    ob_chat_title: 'Chat', ob_chat_desc: 'Group chat for your category and private 1-1 messages with any colleague.',
    ob_directory_title: 'Colleagues', ob_directory_desc: 'Find and contact other healthcare professionals by category and specialty.',
    ob_next: 'Next', ob_prev: 'Back', ob_skip: 'Skip', ob_finish: 'Get started',
    // AI stats
    aiStatsTitle: 'Your AI stats',
    aiStatMyMessages: 'Questions', aiStatSessions: 'Sessions',
    aiStatActiveDays: 'Active days (30d)', aiStatTotalExchanges: 'Total exchanges',
    aiStatLast30d: 'Activity last 30 days',
    aiAdminStatsTitle: 'Global AI stats', aiAdminActiveUsers: 'Active users',
    aiAdminTopUsers: 'Top users by messages',
    settingsAppearance: 'Appearance', settingsAppearanceDesc: 'Customize theme and language',
    settingsAccount: 'Account', settingsAccountDesc: 'Profile, category and security',
    settingsApp: 'App & install', settingsAppDesc: 'Install SanitàNexus on your device',
    settingsSupport: 'Support & info', settingsSupportDesc: 'Feedback, disclaimer and community',
    installApp: 'Install app', installAppDesc: 'Install SanitàNexus as a native app for quick offline access.',
    installAppAvailable: 'Install now', installAppInstalled: 'App already installed',
    installAppNotAvailable: 'Not available on this browser. On iOS use Safari > Share > "Add to Home Screen".',
    aboutApp: 'About SanitàNexus', aboutAppDesc: 'The Italian community for healthcare professionals.',
    versionLabel: 'Version', privacyPolicy: 'Privacy policy', termsOfUse: 'Terms of use',
    sendFeedback: 'Send feedback', contactSupport: 'Contact support',
    quickActions: 'Quick actions',
    pushNotifications: 'Push notifications', pushDesc: 'Receive real-time notifications for messages, comments, initiatives and category updates.',
    pushEnable: 'Enable notifications', pushDisable: 'Disable',
    pushEnabled: 'Notifications enabled ✓', pushDisabled: 'Notifications disabled',
    pushBlocked: 'Notifications blocked by browser. Unblock from site settings.',
    pushNotSupported: 'Your browser does not support push notifications.',
    pushSendTest: 'Send test notification', pushTestSent: 'Test notification sent',
    pushTestNoDevice: 'No device subscribed for this user',
    save: 'Save', unsave: 'Unsave', saved: 'Saved',
    savedAdded: 'Added to saved', savedRemoved: 'Removed from saved',
    savedTitle: 'Saved', savedSubtitle: 'Your saved posts, initiatives and resources',
    savedEmptyTitle: 'Nothing saved yet', savedEmptySub: 'Tap the bookmark icon to save posts, resources and initiatives.',
    savedEmptyPostsTitle: 'No saved posts', savedEmptyPostsSub: 'Save interesting posts from the feed to read later.',
    savedEmptyResourcesTitle: 'No saved resources', savedEmptyResourcesSub: 'Save apps, guides and guidelines from Resources.',
    savedEmptyInitiativesTitle: 'No saved initiatives', savedEmptyInitiativesSub: 'Save job offers and projects from the Board.',
    all: 'All', posts: 'Posts', colleagues: 'Colleagues',
    searchTitle: 'Search', searchPlaceholder: 'Search posts, resources, colleagues, initiatives...',
    searchHint: 'At least 2 characters to start searching',
    searchNoResults: 'No results for your search',
    todayHighlightsTitle: 'Today\u2019s highlights', todayHighlightsSub: 'The best of the community for you',
    trendingPosts: 'Trending posts', latestInitiatives: 'Latest initiatives',
    topResources: 'Top resources', participants: 'participants',
    shiftsTitle: 'Shifts & reminders', shiftsSubtitle: 'Your personal calendar',
    invoicingNav: 'Invoicing',
    consentsNav: 'Consents (AES)',
    refertiNav: 'Encrypted reports',
    portaleNav: 'Encrypted portal',
    validationNav: 'Anti-fraud',
    emailNav: 'Email (SMTP)',
    aiUsageNav: 'AI usage',
    direzioneNav: 'Management',
    backupNav: 'Backup / DR',
    newShift: 'New', editShift: 'Edit',
    shiftTitle: 'Title', shiftTitlePlaceholder: 'E.g. Night shift ward',
    shiftType: 'Type',
    shiftTypeShift: 'Shift', shiftTypeReminder: 'Reminder',
    shiftTypeCourse: 'CME/ECM course', shiftTypeEvent: 'Event',
    startAt: 'Start', endAt: 'End',
    locationOptional: 'Location (optional)', locationPlaceholder: 'E.g. San Raffaele Hospital',
    notesOptional: 'Notes (optional)',
    shiftMissingFields: 'Title and start are required',
    shiftCreated: 'Shift created', shiftUpdated: 'Shift updated',
    shiftDeleted: 'Shift deleted',
    shiftsEmptyTitle: 'No shifts scheduled', shiftsEmptySub: 'Add work shifts, CME courses or personal reminders.',
    upcoming: 'Upcoming', past: 'Past',
    dailyBriefingTitle: 'Good morning! Your daily briefing',
    refresh: 'Refresh',
    analyticsTitle: 'Analytics by category',
    analyticsSubtitle: 'User, post and activity distribution',
    trend7d: '7-day posts trend',
    aiAssistant: 'AI Assistant',
    // Telemedicine (Phase 14)
    telemedNavLabel: 'Telemedicine',
    telemedTitle: 'Telemedicine', telemedSubtitle: 'Book online consultations with certified SanitàNexus doctors',
    telemedDoctorAreaTitle: 'Your Doctor area', telemedDoctorAreaDesc: 'Manage subscription, visit price and availability',
    telemedActive: 'Active', telemedInactive: 'Inactive',
    telemedActivateSubTitle: 'Activate your annual subscription',
    telemedActivateSubDesc: 'To receive bookings and payments from patients, subscribe to the SanitàNexus annual plan (€49.90/year).',
    telemedActivate49: 'Activate subscription • €49.90/year',
    telemedExpiresOn: 'Expires on',
    telemedVisitPrice: 'Visit price (€)',
    telemedPlatformFeeNote: 'Platform fee: 15% per visit',
    telemedAcceptingBookings: 'Accepting bookings?',
    yes: 'Yes', no: 'No',
    telemedConsultationBio: 'Consultation description (visible to patients)',
    telemedConsultationBioPh: 'E.g. Cardiology consultations, arrhythmology and cardiovascular prevention. First full visit 30 minutes.',
    telemedProfileSaved: 'Telemedicine profile updated',
    saving: 'Saving...', processing: 'Processing...',
    genericError: 'An error occurred, please try again',
    telemedFreeCounterTitle: 'Free consultations this month',
    telemedFreeExhausted: 'You\'ve used all free consultations this month. Further bookings will require payment.',
    telemedFreeRemaining: '{{count}} free consultation remaining this month',
    telemedFreeRemaining_other: '{{count}} free consultations remaining this month',
    telemedDoctorsListTitle: 'Available doctors',
    telemedActiveDoctors: 'active doctors',
    telemedNoDoctorsYet: 'No doctors have activated their subscription yet. Doctors can enable it from their personal area.',
    telemedVisit: 'visit', telemedFreeTag: 'Free',
    telemedBookFree: 'Book for free', telemedPayAndBook: 'Book & pay',
    telemedMyAppointments: 'My appointments', telemedNoAppointments: 'No scheduled appointments',
    telemedStatus_confirmed: 'Confirmed', telemedStatus_awaiting_payment: 'Awaiting payment',
    telemedStatus_cancelled: 'Cancelled',
    telemedBookWithDoctor: 'Book with', telemedGenericConsultation: 'Medical consultation',
    telemedThisWillBeFree: 'This consultation is included in your 2 free monthly visits',
    telemedThisWillBePaid: 'Paid consultation (free monthly limit reached)',
    telemedDateTime: 'Date and time',
    telemedReasonOptional: 'Reason for consultation (optional)',
    telemedReasonPh: 'E.g. Blood pressure check, chest pain for 2 days...',




    telemedSubResult: 'Subscription outcome', telemedApptResult: 'Visit outcome',
    telemedSubActivatedTitle: 'Subscription activated!',
    telemedSubActivatedDesc: 'Your annual subscription is now active. You can receive bookings and payments from patients.',
    telemedApptPaidTitle: 'Payment received!',
    telemedApptPaidDesc: 'Your visit is confirmed. You\'ll get the details via notification.',
    telemedPaymentFailedTitle: 'Payment failed',
    telemedPaymentFailedDesc: 'The transaction did not go through. Please retry or use another payment method.',
    telemedPaymentCancelledTitle: 'Payment cancelled',
    telemedPaymentCancelledDesc: 'You cancelled the operation. No charge was made.',
    telemedPaymentTimeoutTitle: 'Verification in progress',
    telemedPaymentTimeoutDesc: 'Verification is taking longer than expected. Refresh in a few minutes or check the appointments list.',
    telemedPaymentErrorTitle: 'Error', telemedPaymentErrorDesc: 'Unable to verify payment status.',
    telemedPaymentCheckingTitle: 'Verifying payment...',
    telemedPaymentCheckingDesc: 'Please wait while we confirm with Stripe.',
    telemedBackToDashboard: 'Back to telemedicine',
    amount: 'Amount',
    // Welcome / Role selection (Task B)
    welcomeChooseTitle: 'Secure Authentication and Restricted Access',
    welcomeChooseSubtitle: 'Select your authentication method to securely access the clinical workspace or the end-to-end encrypted patient portal. Protected on-premise local session.',
    welcomeProfessionalTitle: 'I am a healthcare professional',
    welcomeProfessionalSubtitle: 'For doctors, nurses and healthcare workers',
    welcomeProBullet1: 'Professional feed, category groups and 1-1 chat',
    welcomeProBullet2: 'AI clinical assistant, Italian protocols and guidelines',
    welcomeProBullet3: 'Jobs board, initiatives and CME training',
    welcomeProBullet4: 'Doctor mode: activate Telemedicine and receive payments',
    welcomeProCta: 'Register as professional',
    welcomePatientTitle: 'I am a user / patient',
    welcomePatientSubtitle: 'Looking for an online consultation or visit?',
    welcomePatBullet1: 'Book consultations with verified doctors',
    welcomePatBullet2: '2 free consultations per month, then doctor\'s rate',
    welcomePatBullet3: 'Secure 1-1 chat with your doctor',
    welcomePatBullet4: 'AI health assistant for information and orientation',
    welcomePatCta: 'Register as user/patient',
    welcomeAlreadyRegistered: 'Already have an account?',
    welcomeBackChoice: 'Change access type',
    welcomeBadgeGDPR: 'GDPR compliant',
    welcomeBadgeAI: 'Advanced clinical AI',
    // Patient registration
    patientRegisterTitle: 'Create your User/Patient account',
    patientRegisterSubtitle: 'Only for booking online consultations and visits',
    patientPasswordHint: 'At least 6 characters',
    patientDob: 'Date of birth',
    patientDobOptional: 'Optional — helps the doctor with the consultation',
    patientConsentTitle: 'Health data consent (GDPR)',
    patientConsentBody: 'I authorize SanitàNexus and the doctors I will choose to process my personal and health data for booking, consultation and care purposes, pursuant to EU Regulation 2016/679 (GDPR). Data will not be shared with third parties not involved in the consultation.',
    patientConsentRequired: 'You must accept the health data consent to register',
    patientCreateAccount: 'Create patient account',
    // Patient AI Assistant
    aiPatientNavLabel: 'Health AI',
    aiPatientTitle: 'Health AI Assistant',
    aiPatientSubtitle: 'Nexi, your health information guide',
    aiPatientDisclaimerTitle: 'Important:',
    aiPatientDisclaimerBody: 'I am an informational AI assistant, not a doctor. I do not provide diagnoses or prescriptions. For personal issues book a visit:',
    aiPatientGotoTelemed: 'Go to Telemedicine',
    aiPatientEmptyTitle: 'Ask me anything about your health',
    aiPatientEmptyDesc: 'I can explain common symptoms, OTC medications, tests, prevention, when to consult a specialist.',
    aiPatientPlaceholder: 'Write your question... (e.g. what to do for a persistent headache)',
    aiPatientSugSymptoms: 'I\'ve had a headache for 3 days, when should I worry?',
    aiPatientSugMed: 'Can I take ibuprofen if I have gastritis?',
    aiPatientSugPrevention: 'What check-ups should I do at 40?',
    aiPatientSugSpecialist: 'Which specialist should I see for sleep problems?',
    aiPatientBookHint: 'For a personal consultation with a doctor:',
    aiPatientBookCta: 'Book a visit',
    aiNewChat: 'New chat',
    aiNoHistoryYet: 'No conversations yet',
    aiUntitledSession: 'Chat',
    aiMessagesCount: 'messages',
    patientHealthAppsNav: 'Health Apps',
    healthDataNav: 'IoT Data',
    myServicesNav: 'Services',
    proToolkitNav: 'Pro Toolkit',
    agenticHealthNav: 'AI Assistant',
    voiceDiaryNav: 'Voice Diary',
    fseNav: 'FSE Lombardia',
    ecmNav: 'ECM Assistant',
    woundCareNav: 'Wound Care',
    careTeamNav: 'Care Team',
    patientHealthAppsSubtitle: 'Official Italian healthcare apps, drug databases and vital-signs monitoring apps.',
    // Premium
    premiumTitle: 'Upgrade to Premium',
    premiumActiveTitle: 'Premium active',
    premiumActive: 'Active',
    premiumActiveDesc: 'Your Premium plan is active. Next renewal: {{date}}.',
    premiumPitch: 'For only €{{price}}/month unlock more free consultations, discount on visits and unlimited Health AI.',
    premiumBenefit1: '{{count}} free consultations per month (vs 2 standard)',
    premiumBenefit2: '{{pct}}% off on all paid visits',
    premiumBenefit3: 'Unlimited Health AI Assistant (Nexi)',
    premiumBenefit4: 'Cancel anytime',
    premiumCTA: 'Activate Premium • €{{price}}/month',
    premiumResult: 'Premium result',
    premiumActivatedTitle: 'Premium activated!',
    premiumActivatedDesc: 'You now have 5 free consultations/month, 10% off on visits and unlimited Health AI. Monthly renewal.',

    // Booking Wizard (Sprint 2)
    bookStep1: 'Date & time', bookStep2: 'Triage', bookStep3: 'Summary',
    bookPickDay: 'Pick a day', bookPickTime: 'Available time',
    bookNoSlotsForDay: 'No slots available on this day',
    bookTryAnotherDay: 'Try another day or contact the doctor',
    bookUrgency: 'Urgency level',
    urgencyRoutine: 'Routine', urgencyRoutineDesc: 'Non-urgent check or consultation',
    urgencyUrgent: 'Urgent', urgencyUrgentDesc: 'Symptoms needing attention soon',
    urgencyEmergency: 'Emergency', urgencyEmergencyDesc: 'Acute symptoms — top priority',
    triageIntro: 'Answer briefly to help the doctor prepare for your visit.',
    triageSymptoms: 'Main symptoms', triageSymptomsPh: 'E.g. Headache and nausea for 2 days',
    triageDuration: 'Duration', triageDurationPh: 'E.g. 3 days',
    triageSeverity: 'Intensity', triageAllergies: 'Known allergies', triageAllergiesPh: 'E.g. Penicillin, peanuts',
    triageCurrentMeds: 'Current medications', triageCurrentMedsPh: 'E.g. Cardio-aspirin 100mg',
    bookSummaryTitle: 'Booking summary', bookSummaryDoctor: 'Doctor',
    bookSummaryDateTime: 'Date & time', bookSummaryUrgency: 'Urgency', bookSummaryPrice: 'Price',
    bookPaymentNote: 'You will be redirected to Stripe to securely complete payment.',
    telemedBookingConfirmed: 'Booking confirmed!',
    telemedConfirmFreeBooking: 'Confirm booking', telemedProceedToPayment: 'Proceed to payment',
    telemedPickDateTime: 'Pick a date and time', back: 'Back', next: 'Next',
    telemedSearchPlaceholder: 'Search doctor or specialty…',
    telemedFilterBySpec: 'Filter by specialty', telemedAllSpecs: 'All',
    telemedNoDoctorsMatch: 'No doctors match your filters',
    bookJoinNow: 'Join now', bookInVisitNow: 'Enter visit',

    // Doctor Command Center (Sprint 3)
    doctorAgendaTitle: 'Agenda', doctorAgendaToday: 'Back to today',
    doctorNoApptsToday: 'No appointments this day',
    doctorAllUrgencies: 'All',
    doctorPanelSelectPatient: 'Select an appointment',
    doctorPanelSelectPatientDesc: 'Click on an appointment in the timeline to see triage and patient card',
    doctorTriageTitle: 'Pre-visit triage',
    doctorViewFullHistory: 'View full patient history',
    doctorKpiToday: 'Today', doctorKpiWeek: 'Next 7 days',
    doctorKpiCompleted: 'Completed', doctorKpiVisitPrice: 'Visit price',
    availabilityBtn: 'Availability',
    availabilityTitle: 'Weekly availability',
    availabilityDesc: 'Set which days and hours you accept bookings',
    availabilitySlotMin: 'Slot length (min)', availabilityBufferMin: 'Buffer between visits (min)',
    availabilityDays: 'Days & hours', availabilityClosed: 'Closed',
    availabilitySaved: 'Availability updated',

    // Video Room (Sprint 4)
    videoLive: 'Live', videoConnected: 'Connected',
    videoWaitingDoctor: 'Waiting for doctor to start…',
    videoWaitingDoctorLong: 'Waiting for the doctor to start the visit. We\'ll connect you automatically.',
    videoYou: 'You', videoE2ENotice: 'Encrypted session',
    videoMockNotice: 'MVP preview — real video connection will be enabled soon',
    visitStart: 'Start visit', visitStarted: 'Visit started',
    visitEnded: 'Visit ended',
    notesTitle: 'Clinical notes', notesClinical: 'Visit notes',
    notesClinicalPh: 'Summary, history, findings…',
    notesDiagnosis: 'Diagnosis (optional)', notesDiagnosisPh: 'E.g. Tension headache',
    notesPrescriptions: 'Prescriptions (optional)', notesPrescriptionsPh: 'E.g. Paracetamol 1g x 3/day',
    notesSave: 'Save note', noteSaved: 'Clinical note saved',
    notesHistory: 'Previous notes',
    // Waiting room
    waitTitle: 'Virtual waiting room',
    waitScheduled: 'Scheduled at: {{time}}',
    waitCountdown: 'Time to start',
    waitStartingNow: 'Starting now',
    waitCheckAgain: 'Refresh status',
    waitSecurityNote: 'End-to-end encrypted session — no recordings stored.',
    waitTip1: 'Find a quiet, well-lit space for the video call.',
    waitTip2: 'If possible, keep your recent lab results and reports handy.',
    waitTip3: 'Prepare a list of medications you are taking, with doses and frequency.',
    waitTip4: 'Drink water and stay calm — the doctor is ready to help.',
    waitTip5: 'Reminder: for emergencies always call 112 (or 118 for medical emergencies).',

    // Sprint A - Trust & Security
    menuSecurity: 'Security & Privacy',
    menuWallet: 'Documents (Wallet)',
    securityBadge: 'Security & Privacy',
    securityTitle: 'Your security center',
    securitySubtitle: 'Manage professional verification, two-factor auth, GDPR consents and access history.',
    securityChipAlbo: 'Registry verification',
    securityChip2FA: '2FA authentication',
    securityChipGDPR: 'GDPR consents',
    securityChipAudit: 'Audit log',

    alboTitle: 'Registry verification',
    alboSubtitle: 'Show patients that you are a verified professional.',
    alboProfession: 'Profession', alboNumber: 'Registry no.', alboProvince: 'Province',
    alboVerified: 'Verified', alboPending: 'Pending', alboNotSet: 'Not set',
    alboVerifyNow: 'Verify now', alboReverify: 'Update verification',
    alboVerifiedInfo: 'Registered no. {{num}} — province {{prov}} (auto-verified by SanitàNexus).',
    alboVerifiedSuccess: 'Verification complete! You now appear as verified.',
    alboVerifiedFailed: 'Verification failed',

    twoFATitle: 'Two-factor authentication',
    twoFASubtitle: 'Add a temporary code from your authenticator app (Google Authenticator, Authy, 1Password).',
    twoFAOn: 'Enabled', twoFAOff: 'Disabled', twoFAStart: 'Enable 2FA',
    twoFAWhyTitle: 'Why enable?',
    twoFAWhyDesc: 'Protects your account even if someone knows your password. Mandatory for doctors accessing clinical data.',
    twoFAScanTitle: 'Scan with your app',
    twoFAScanDesc: 'Or manually copy the secret below. Then enter the 6-digit code generated.',
    twoFAEnterCode: '6-digit code',
    twoFAConfirm: 'Confirm and enable',
    twoFAEnabledSuccess: '2FA enabled!',
    twoFADisabledSuccess: '2FA disabled',
    twoFAActiveTitle: '2FA protects your account',
    twoFAActiveDesc: 'On next login we will ask for the code from your authenticator app.',
    twoFADisable: 'Disable 2FA',
    twoFADisableCode: 'Confirm with 2FA code to disable',
    twoFASecretCopied: 'Secret copied',
    login2FATitle: '2FA verification',
    login2FADesc: 'Enter the 6-digit code from your authenticator app.',
    login2FACode: 'Code', login2FAVerify: 'Verify',
    login2FAPrompt: 'Enter 2FA code to complete login',
    login2FAWrong: 'Invalid 2FA code',

    consentTitle: 'Privacy consents (GDPR)',
    consentSubtitle: 'Granular control over how we use your data. You can revoke at any time.',
    consentRequired: 'Required', consentSaved: 'Consents updated',
    consentNoChanges: 'No changes to save',
    consentLastUpdated: 'Last updated: {{date}}',

    auditTitle: 'Access history',
    auditSubtitle: 'Every sensitive action on your account is recorded immutably.',
    auditEmpty: 'No activity yet',

    walletBadge: 'Digital Wallet',
    walletTitleHero: 'Your medical documents',
    walletSubtitleHero: 'Prescriptions, reports, exams and certificates — always with you, like Apple Wallet.',
    walletAdd: 'Add document',
    walletPrescriptionsTitle: 'Prescriptions',
    walletDocumentsTitle: 'Clinical documents',
    walletNoPrescriptions: 'No prescriptions yet. Prescriptions issued by doctors during visits will appear here automatically.',
    walletNoDocuments: 'No documents yet. Upload reports, exams or certificates from your phone.',
    walletUploadTitle: 'Upload document',
    walletUploadDesc: 'Add a report, exam or certificate to your wallet.',
    walletUploadSuccess: 'Document uploaded',
    walletTitle: 'Title', walletTitlePh: 'E.g. Blood work',
    walletDocType: 'Document type',
    walletDescription: 'Description (optional)', walletDescriptionPh: 'Additional notes…',
    walletFile: 'File', walletDropOrClick: 'Click to upload a file',
    walletFileHint: 'PDF or images, max 4MB',
    walletUpload: 'Upload', walletDownload: 'Download',
    walletConfirmDelete: 'Really delete this document?',
    walletDeleted: 'Document deleted',

    rxTitle: 'New electronic prescription',
    rxSubtitle: 'Create a prescription with authentication QR — instantly available in patient wallet.',
    rxDiagnosis: 'Diagnosis (optional)', rxDiagnosisPh: 'E.g. Tension headache',
    rxDrugs: 'Medications', rxDrugName: 'Medication *',
    rxDrugDosage: 'Dosage (e.g. 1g)', rxDrugFrequency: 'Frequency (e.g. 3x/day)',
    rxDrugDuration: 'Duration (e.g. 5 days)',
    rxDrugNotes: 'Notes for this drug',
    rxAddDrug: 'Add medication',
    rxNotes: 'General notes', rxNotesPh: 'E.g. Drink plenty of water, follow-up in 7 days',
    rxCreate: 'Issue prescription', rxCreated: 'Prescription issued!',
    rxCreatedTitle: 'Prescription issued',
    rxCreatedDesc: 'The patient will see it automatically in their wallet. You can also download the PDF.',
    rxCodeInfo: 'The QR code allows document authenticity verification.',
    rxDownloadPdf: 'Download PDF', rxCreateAnother: 'New prescription',

    // Password reset flow
    pwResetForgotLink: 'Forgot password?',
    pwResetTitle: 'Recover password',
    pwResetSubtitle: 'Enter your account email. We\'ll send you a link to reset your password.',
    pwResetSendLink: 'Send recovery link',
    pwResetBackToLogin: 'Back to login',
    pwResetEmailSent: 'Check your email',
    pwResetEmailSentDesc: 'If the address is registered, you\'ll receive an email with a link to reset your password. The link expires in 15 minutes.',
    pwResetTryAnother: 'Use another email',
    pwResetDevTitle: 'Developer mode',
    pwResetDevDesc: 'Email sending is not configured in this environment. Use the link below to test the flow.',
    pwResetDevCopied: 'Link copied to clipboard',
    pwResetInvalidTitle: 'Invalid link',
    pwResetInvalidLink: 'The link is expired or invalid.',
    pwResetRequestNew: 'Request a new link',
    pwResetNewTitle: 'Set new password',
    pwResetNewFor: 'Resetting password for {{email}}',
    pwResetNewPassword: 'New password (min. 8 chars)',
    pwResetConfirm: 'Confirm password',
    pwResetMismatch: 'Passwords do not match',
    pwResetSetPassword: 'Update password',
    pwResetSuccess: 'Password updated! Redirecting to login...',
    pwResetDoneTitle: 'Password updated',
    pwResetDoneDesc: 'Your password was changed successfully. Redirecting to login.',
    pwResetSecurityNote: 'For your security, all active sessions will remain until you log in with the new password.',
    pwStrengthWeak: 'Weak', pwStrengthMedium: 'Medium', pwStrengthStrong: 'Strong',
  },
  es: {
    appName: 'SanitàNexus', tagline: 'La comunidad de profesionales sanitarios',
    login: 'Iniciar sesión', register: 'Registrarse', email: 'Correo', password: 'Contraseña',
    fullName: 'Nombre completo', specialty: 'Especialidad', bio: 'Biografía',
    websiteUrl: 'Sitio web', linkedinUrl: 'Perfil de LinkedIn',
    category: 'Categoría', medici: 'Médicos', infermieri: 'Enfermeros', oss: 'Trabajadores Sanitarios',
    medico: 'Médico', infermiere: 'Enfermero', operatoreSS: 'Trabajador Sanitario', logout: 'Cerrar sesión',
    feed: 'Feed', bacheca: 'Tablón', chat: 'Chat', directory: 'Colegas',
    profile: 'Perfil', settings: 'Ajustes', resources: 'Recursos',
    notifications: 'Notificaciones', admin: 'Admin',
    generale: 'General', newPost: 'Nueva publicación', writeSomething: 'Escribe algo...',
    addImages: 'Agregar imágenes', youtubeLink: 'Enlace de YouTube (opcional)',
    audience: 'Audiencia', publish: 'Publicar', cancel: 'Cancelar',
    like: 'Me gusta', comment: 'Comentar', comments: 'Comentarios',
    addComment: 'Añade un comentario...', send: 'Enviar',
    shareIdea: 'Comparte tu idea, tutorial o experiencia',
    loginToPost: 'Inicia sesión para publicar', posts: 'Publicaciones',
    audienceAll: 'Todos', audienceSpecific: 'Solo mi categoría',
    initiatives: 'Iniciativas', initiative: 'Iniciativa',
    helpsProjects: 'Ayuda & Proyectos', help: 'Ayuda', project: 'Proyecto',
    jobs: 'Empleo', job: 'Empleo',
    free: 'Gratis', paid: 'De pago', join: 'Unirse', leave: 'Salir',
    participants: 'Participantes', createInitiative: 'Nuevo anuncio',
    title: 'Título', description: 'Descripción', location: 'Ubicación',
    startDate: 'Fecha inicio', endDate: 'Fecha fin', price: 'Precio',
    isPaid: 'De pago', deadline: 'Fecha límite', contactInfo: 'Contacto', type: 'Tipo',
    groupChat: 'Chat de grupo', privateChats: 'Chats privados', typeMessage: 'Escribe un mensaje...',
    groupChatDesc: 'Conecta con todos tus colegas de la misma categoría',
    search: 'Buscar', searchProfessionals: 'Buscar profesionales...',
    allProfessionals: 'Todos los profesionales',
    message: 'Mensaje', editProfile: 'Editar perfil', save: 'Guardar',
    language: 'Idioma', italian: 'Italiano', english: 'Inglés',
    spanish: 'Español', german: 'Alemán',
    theme: 'Tema', light: 'Claro', dark: 'Oscuro', system: 'Sistema',
    createResource: 'Añadir recurso', link: 'Enlace URL',
    pharmacology: 'Farmacología', procedure: 'Procedimiento', guideline: 'Guía',
    app: 'App', other: 'Otro', wellness: 'Bienestar', protocol_it: 'Protocolos IT',
    national_app: 'Apps Nacionales',
    open: 'Abrir', upvote: 'Útil',
    resourcesDesc: 'Enlaces a fármacos, procedimientos, guías y apps útiles',
    all: 'Todo', viewAll: 'Ver todos',
    aiAssistant: 'Asistente Clínico IA', aiPoweredBy: 'Con Claude Sonnet 4.5',
    aiDisclaimer: 'Asistente IA con fines informativos. NO es un dispositivo médico y NO sustituye el diagnóstico ni el juicio clínico. Verifica en fuentes oficiales.',
    aiWelcome: '¿En qué puedo ayudarte?',
    aiWelcomeDesc: 'Pregunta sobre fármacos, protocolos, procedimientos, cálculos, guías y actualizaciones.',
    aiInputPlaceholder: 'Haz una pregunta clínica...', aiThinking: 'Pensando...',
    newChat: 'Nueva chat',
    feedbackTitle: 'Feedback & Co-diseño',
    feedbackSubtitle: 'Sugiere mejoras. Tu opinión guía el desarrollo.',
    newFeedback: 'Nuevo feedback', feedbackCategory: 'Categoría',
    fb_feature: 'Función', fb_bug: 'Bug', fb_ux: 'UX', fb_other: 'Otro',
    fb_status_open: 'Abierto', fb_status_in_progress: 'En curso',
    fb_status_planned: 'Planeado', fb_status_done: 'Hecho', fb_status_wont_do: 'Rechazado',
    adminNotes: 'Notas admin',
    privacy: 'Privacidad', privacyEmail: 'Quién ve tu correo',
    privacyPhone: 'Quién ve tu teléfono', privacySpecialty: 'Quién ve tu especialidad',
    privacy_public: 'Todos', privacy_colleagues: 'Colegas (misma categoría)', privacy_private: 'Nadie',
    phone: 'Teléfono',
    wellnessSubtitle: 'Apoyo psicológico y económico para sanitarios en burnout',
    burnoutSelfAssessment: 'Auto-evaluación del burnout',
    burnoutAssessmentDesc: 'Cuestionario anónimo breve (basado en MBI).',
    startAssessment: 'Comenzar',
    answerHonestly: 'Responde con honestidad según cómo te has sentido el último mes',
    assessmentDisclaimer: 'Esta auto-evaluación es orientativa y no sustituye un diagnóstico profesional.',
    overallRisk: 'Riesgo general', retake: 'Repetir',
    risk_low: 'Riesgo bajo', risk_moderate: 'Riesgo moderado', risk_high: 'Riesgo alto',
    emotionalExhaustion: 'Agotamiento emocional',
    depersonalization: 'Despersonalización',
    personalAccomplishment: 'Realización personal',
    recommendation_low: 'Muy bien. Sigue cuidándote con pausas regulares y apoyo social.',
    recommendation_moderate: 'Atento a señales de estrés. Considera pausas más largas y hablar con un colega de confianza.',
    recommendation_high: 'Los resultados indican alto riesgo. Contacta a un profesional o al servicio de apoyo de tu Colegio.',
    supportResources: 'Recursos de apoyo', italianProtocols: 'Protocolos italianos',
    supportRequests: 'Solicitudes de apoyo', addSupportRequest: 'Añadir solicitud',
    noSupportRequests: 'Sin solicitudes de momento',
    scaleNever: 'Nunca', scaleFewYear: 'Pocas veces al año', scaleMonthly: 'Una vez al mes',
    scaleFewMonth: 'Pocas veces al mes', scaleWeekly: 'Una vez a la semana',
    scaleFewWeek: 'Pocas veces a la semana', scaleDaily: 'Cada día',
    q_ee1: 'Me siento emocionalmente agotado/a por mi trabajo',
    q_ee2: 'Me siento agotado/a al final de la jornada',
    q_ee3: 'Me siento cansado/a al levantarme por la mañana',
    q_ee4: 'Trabajar todo el día con personas es un esfuerzo para mí',
    q_dp1: 'Trato a algunos pacientes como objetos impersonales',
    q_dp2: 'Me he vuelto más insensible desde que hago este trabajo',
    q_dp3: 'Temo que este trabajo me esté endureciendo emocionalmente',
    q_pa1: 'Comprendo fácilmente lo que sienten mis pacientes',
    q_pa2: 'Afronto los problemas emocionales con calma',
    q_pa3: 'Me siento lleno/a de energía positiva',
    markAllRead: 'Marcar todo como leído', noNotifications: 'Sin notificaciones',
    report: 'Denunciar', reportReason: 'Motivo de la denuncia',
    reports: 'Denuncias', pending: 'Pendientes', resolved: 'Resueltas',
    dismiss: 'Descartar', deleteTarget: 'Eliminar contenido', reportedBy: 'Denunciado por',
    adminPanel: 'Panel admin', stats: 'Estadísticas',
    totalUsers: 'Usuarios totales', totalPosts: 'Publicaciones totales',
    totalMessages: 'Mensajes totales', pendingReports: 'Denuncias pendientes',
    welcome: 'Bienvenido', welcomeBack: 'Bienvenido de nuevo',
    alreadyHaveAccount: '¿Ya tienes cuenta?', dontHaveAccount: '¿No tienes cuenta?',
    loginNow: 'Iniciar sesión', registerNow: 'Regístrate',
    noPostsYet: 'Aún no hay publicaciones', beFirstToPost: 'Sé el primero en compartir',
    noConversations: 'Sin conversaciones', noInitiatives: 'Sin anuncios',
    noResources: 'Sin recursos', noResults: 'Sin resultados',
    loading: 'Cargando...', confirm: 'Confirmar', delete: 'Eliminar', remove: 'Quitar',
    loginSuccess: 'Sesión iniciada', registerSuccess: 'Registro completado',
    postCreated: 'Publicación creada', initiativeCreated: 'Anuncio creado',
    resourceCreated: 'Recurso añadido', reportSent: 'Denuncia enviada',
    errorOccurred: 'Ocurrió un error',
    invalidCredentials: 'Credenciales inválidas', emailInUse: 'Correo ya registrado',
    selectCategory: 'Selecciona una categoría', professionalCategory: 'Categoría profesional',
    images: 'Imágenes', startChat: 'Iniciar chat', viewProfile: 'Ver perfil',
    noSpecialty: 'Sin especialidad', noBio: 'Sin biografía',
    minutesAgo: 'hace {{count}} min', hoursAgo: 'hace {{count}} h',
    daysAgo: 'hace {{count}} d', justNow: 'Ahora mismo',
    confirmDelete: '¿Seguro que quieres eliminar?',
    allCategories: 'Todas las categorías', filterCategory: 'Filtrar por categoría',
    filterType: 'Filtrar por tipo', resolveAction: 'Resolver',
    ob_welcome_title: 'Bienvenido a SanitàNexus', ob_welcome_desc: 'La comunidad para médicos, enfermeros y auxiliares. Un tour de 30 segundos.',
    ob_feed_title: 'Feed', ob_feed_desc: 'Tu feed personal con publicaciones, tutoriales y experiencias de colegas.',
    ob_bacheca_title: 'Tablón', ob_bacheca_desc: 'Iniciativas, ofertas de empleo, cursos y proyectos.',
    ob_ai_title: 'Asistente Clínico IA', ob_ai_desc: 'Preguntas sobre fármacos, protocolos y guías. Respuestas de Claude Sonnet 4.5.',
    ob_wellness_title: 'Bienestar y Burnout', ob_wellness_desc: 'Auto-evaluación anónima (MBI) y recursos de apoyo.',
    ob_resources_title: 'Recursos clínicos', ob_resources_desc: 'Farmacología, procedimientos, guías, apps nacionales y otros recursos verificados.',
    ob_chat_title: 'Chat', ob_chat_desc: 'Chat de grupo por categoría y mensajes privados 1-1.',
    ob_directory_title: 'Colegas', ob_directory_desc: 'Encuentra y contacta a otros profesionales de la salud.',
    ob_next: 'Siguiente', ob_prev: 'Atrás', ob_skip: 'Saltar', ob_finish: 'Comenzar',
    aiStatsTitle: 'Tus estadísticas IA',
    aiStatMyMessages: 'Preguntas', aiStatSessions: 'Sesiones',
    aiStatActiveDays: 'Días activos (30d)', aiStatTotalExchanges: 'Intercambios totales',
    aiStatLast30d: 'Actividad últimos 30 días',
    aiAdminStatsTitle: 'Estadísticas IA globales', aiAdminActiveUsers: 'Usuarios activos',
    aiAdminTopUsers: 'Top usuarios por mensajes',
  },
  de: {
    appName: 'SanitàNexus', tagline: 'Die Community für Gesundheitsfachkräfte',
    login: 'Anmelden', register: 'Registrieren', email: 'E-Mail', password: 'Passwort',
    fullName: 'Vollständiger Name', specialty: 'Fachgebiet', bio: 'Bio',
    websiteUrl: 'Webseite', linkedinUrl: 'LinkedIn-Profil',
    category: 'Kategorie', medici: 'Ärzte', infermieri: 'Pflegekräfte', oss: 'Gesundheitshelfer',
    medico: 'Arzt', infermiere: 'Pflegekraft', operatoreSS: 'Gesundheitshelfer', logout: 'Abmelden',
    feed: 'Feed', bacheca: 'Board', chat: 'Chat', directory: 'Kollegen',
    profile: 'Profil', settings: 'Einstellungen', resources: 'Ressourcen',
    notifications: 'Benachrichtigungen', admin: 'Admin',
    generale: 'Allgemein', newPost: 'Neuer Beitrag', writeSomething: 'Schreibe etwas...',
    addImages: 'Bilder hinzufügen', youtubeLink: 'YouTube-Link (optional)',
    audience: 'Zielgruppe', publish: 'Veröffentlichen', cancel: 'Abbrechen',
    like: 'Gefällt mir', comment: 'Kommentieren', comments: 'Kommentare',
    addComment: 'Kommentar hinzufügen...', send: 'Senden',
    shareIdea: 'Teile deine Idee, dein Tutorial oder deine Erfahrung',
    loginToPost: 'Anmelden zum Posten', posts: 'Beiträge',
    audienceAll: 'Alle', audienceSpecific: 'Nur meine Kategorie',
    initiatives: 'Initiativen', initiative: 'Initiative',
    helpsProjects: 'Hilfe & Projekte', help: 'Hilfe', project: 'Projekt',
    jobs: 'Jobs', job: 'Job',
    free: 'Kostenlos', paid: 'Kostenpflichtig', join: 'Beitreten', leave: 'Verlassen',
    participants: 'Teilnehmer', createInitiative: 'Neuer Eintrag',
    title: 'Titel', description: 'Beschreibung', location: 'Ort',
    startDate: 'Startdatum', endDate: 'Enddatum', price: 'Preis',
    isPaid: 'Kostenpflichtig', deadline: 'Frist', contactInfo: 'Kontakt', type: 'Typ',
    groupChat: 'Gruppenchat', privateChats: 'Private Chats', typeMessage: 'Nachricht schreiben...',
    groupChatDesc: 'Verbinde dich mit allen Kollegen deiner Kategorie',
    search: 'Suchen', searchProfessionals: 'Fachkräfte suchen...',
    allProfessionals: 'Alle Fachkräfte',
    message: 'Nachricht', editProfile: 'Profil bearbeiten', save: 'Speichern',
    language: 'Sprache', italian: 'Italienisch', english: 'Englisch',
    spanish: 'Spanisch', german: 'Deutsch',
    theme: 'Theme', light: 'Hell', dark: 'Dunkel', system: 'System',
    createResource: 'Ressource hinzufügen', link: 'URL-Link',
    pharmacology: 'Pharmakologie', procedure: 'Prozedur', guideline: 'Leitlinie',
    app: 'App', other: 'Sonstiges', wellness: 'Wohlbefinden', protocol_it: 'IT Protokolle',
    national_app: 'Nationale Apps',
    open: 'Öffnen', upvote: 'Nützlich',
    resourcesDesc: 'Links zu Medikamenten, Prozeduren, Leitlinien & nützlichen Apps',
    all: 'Alle', viewAll: 'Alle anzeigen',
    aiAssistant: 'Klinischer KI-Assistent', aiPoweredBy: 'Powered by Claude Sonnet 4.5',
    aiDisclaimer: 'KI-Assistent nur zu Informationszwecken. KEIN Medizinprodukt und ersetzt KEINE Diagnose. Prüfe Informationen stets auf offiziellen Quellen.',
    aiWelcome: 'Wie kann ich helfen?',
    aiWelcomeDesc: 'Frage nach Medikamenten, Protokollen, Prozeduren, klinischen Berechnungen, Leitlinien.',
    aiInputPlaceholder: 'Stelle eine klinische Frage...', aiThinking: 'Denke nach...',
    newChat: 'Neuer Chat',
    feedbackTitle: 'Feedback & Co-Design',
    feedbackSubtitle: 'Schlage Verbesserungen vor. Deine Meinung leitet die Entwicklung.',
    newFeedback: 'Neues Feedback', feedbackCategory: 'Kategorie',
    fb_feature: 'Funktion', fb_bug: 'Bug', fb_ux: 'UX', fb_other: 'Sonstiges',
    fb_status_open: 'Offen', fb_status_in_progress: 'In Bearbeitung',
    fb_status_planned: 'Geplant', fb_status_done: 'Erledigt', fb_status_wont_do: 'Abgelehnt',
    adminNotes: 'Admin-Notizen',
    privacy: 'Privatsphäre', privacyEmail: 'Wer sieht deine E-Mail',
    privacyPhone: 'Wer sieht dein Telefon', privacySpecialty: 'Wer sieht dein Fachgebiet',
    privacy_public: 'Alle', privacy_colleagues: 'Kollegen (gleiche Kategorie)', privacy_private: 'Niemand',
    phone: 'Telefon',
    wellnessSubtitle: 'Psychologische und finanzielle Unterstützung für Pflegekräfte im Burnout',
    burnoutSelfAssessment: 'Burnout-Selbsteinschätzung',
    burnoutAssessmentDesc: 'Kurzer anonymer Fragebogen (basiert auf MBI).',
    startAssessment: 'Starten',
    answerHonestly: 'Antworte ehrlich basierend auf dem letzten Monat',
    assessmentDisclaimer: 'Diese Selbsteinschätzung ist indikativ und ersetzt keine professionelle Diagnose.',
    overallRisk: 'Gesamtrisiko', retake: 'Wiederholen',
    risk_low: 'Geringes Risiko', risk_moderate: 'Mittleres Risiko', risk_high: 'Hohes Risiko',
    emotionalExhaustion: 'Emotionale Erschöpfung',
    depersonalization: 'Depersonalisierung',
    personalAccomplishment: 'Persönliche Leistung',
    recommendation_low: 'Gut! Achte weiter auf dich mit Pausen und sozialer Unterstützung.',
    recommendation_moderate: 'Achte auf Stresssignale. Erwäge längere Pausen und sprich mit einer vertrauten Kollegin.',
    recommendation_high: 'Ergebnisse deuten auf hohes Burnout-Risiko hin. Kontaktiere eine Fachperson oder den Support-Service.',
    supportResources: 'Unterstützungsressourcen', italianProtocols: 'Italienische Protokolle',
    supportRequests: 'Support-Anfragen', addSupportRequest: 'Anfrage hinzufügen',
    noSupportRequests: 'Aktuell keine Anfragen',
    scaleNever: 'Nie', scaleFewYear: 'Ein paar Mal im Jahr', scaleMonthly: 'Einmal im Monat',
    scaleFewMonth: 'Ein paar Mal im Monat', scaleWeekly: 'Einmal pro Woche',
    scaleFewWeek: 'Ein paar Mal pro Woche', scaleDaily: 'Jeden Tag',
    q_ee1: 'Ich fühle mich durch meine Arbeit emotional erschöpft',
    q_ee2: 'Am Ende des Arbeitstages fühle ich mich erschöpft',
    q_ee3: 'Ich fühle mich morgens beim Aufstehen bereits müde',
    q_ee4: 'Den ganzen Tag mit Menschen zu arbeiten ist anstrengend für mich',
    q_dp1: 'Ich behandle manche Patienten wie unpersönliche Objekte',
    q_dp2: 'Ich bin gegenüber Menschen abgestumpfter geworden',
    q_dp3: 'Ich befürchte, dass diese Arbeit mich emotional härtet',
    q_pa1: 'Ich verstehe leicht, wie sich meine Patienten fühlen',
    q_pa2: 'Ich gehe ruhig mit emotionalen Problemen um',
    q_pa3: 'Ich fühle mich voller positiver Energie',
    markAllRead: 'Alle als gelesen markieren', noNotifications: 'Keine Benachrichtigungen',
    report: 'Melden', reportReason: 'Grund der Meldung',
    reports: 'Meldungen', pending: 'Ausstehend', resolved: 'Erledigt',
    dismiss: 'Verwerfen', deleteTarget: 'Inhalt löschen', reportedBy: 'Gemeldet von',
    adminPanel: 'Admin-Panel', stats: 'Statistiken',
    totalUsers: 'Nutzer gesamt', totalPosts: 'Beiträge gesamt',
    totalMessages: 'Nachrichten gesamt', pendingReports: 'Offene Meldungen',
    welcome: 'Willkommen', welcomeBack: 'Willkommen zurück',
    alreadyHaveAccount: 'Bereits ein Konto?', dontHaveAccount: 'Noch kein Konto?',
    loginNow: 'Anmelden', registerNow: 'Registrieren',
    noPostsYet: 'Noch keine Beiträge', beFirstToPost: 'Sei der Erste, der teilt',
    noConversations: 'Keine Unterhaltungen', noInitiatives: 'Keine Einträge',
    noResources: 'Keine Ressourcen', noResults: 'Keine Ergebnisse',
    loading: 'Wird geladen...', confirm: 'Bestätigen', delete: 'Löschen', remove: 'Entfernen',
    loginSuccess: 'Angemeldet', registerSuccess: 'Registrierung erfolgreich',
    postCreated: 'Beitrag veröffentlicht', initiativeCreated: 'Eintrag erstellt',
    resourceCreated: 'Ressource hinzugefügt', reportSent: 'Meldung gesendet',
    errorOccurred: 'Ein Fehler ist aufgetreten',
    invalidCredentials: 'Ungültige Anmeldedaten', emailInUse: 'E-Mail bereits verwendet',
    selectCategory: 'Kategorie wählen', professionalCategory: 'Berufskategorie',
    images: 'Bilder', startChat: 'Chat starten', viewProfile: 'Profil ansehen',
    noSpecialty: 'Kein Fachgebiet', noBio: 'Keine Bio',
    minutesAgo: 'vor {{count}} Min.', hoursAgo: 'vor {{count}} Std.',
    daysAgo: 'vor {{count}} T.', justNow: 'Gerade eben',
    confirmDelete: 'Wirklich löschen?',
    allCategories: 'Alle Kategorien', filterCategory: 'Nach Kategorie filtern',
    filterType: 'Nach Typ filtern', resolveAction: 'Erledigen',
    ob_welcome_title: 'Willkommen bei SanitàNexus', ob_welcome_desc: 'Die Community für Ärzte, Pflegekräfte und Gesundheitshelfer. Ein 30-Sekunden-Rundgang.',
    ob_feed_title: 'Feed', ob_feed_desc: 'Dein persönlicher Feed mit Beiträgen und Erfahrungen von Kollegen.',
    ob_bacheca_title: 'Board', ob_bacheca_desc: 'Initiativen, Jobangebote, Kurse und Projekte.',
    ob_ai_title: 'Klinischer KI-Assistent', ob_ai_desc: 'Fragen zu Medikamenten, Protokollen und Leitlinien. Sofortige Antworten mit Claude Sonnet 4.5.',
    ob_wellness_title: 'Wohlbefinden & Burnout', ob_wellness_desc: 'Anonyme MBI-Selbsteinschätzung und Unterstützungsressourcen.',
    ob_resources_title: 'Klinische Ressourcen', ob_resources_desc: 'Pharmakologie, Prozeduren, Leitlinien und nationale Apps.',
    ob_chat_title: 'Chat', ob_chat_desc: 'Gruppenchat und private 1-1-Nachrichten.',
    ob_directory_title: 'Kollegen', ob_directory_desc: 'Andere Gesundheitsfachkräfte finden und kontaktieren.',
    ob_next: 'Weiter', ob_prev: 'Zurück', ob_skip: 'Überspringen', ob_finish: 'Loslegen',
    aiStatsTitle: 'Deine KI-Statistiken',
    aiStatMyMessages: 'Fragen', aiStatSessions: 'Sitzungen',
    aiStatActiveDays: 'Aktive Tage (30T)', aiStatTotalExchanges: 'Austausche gesamt',
    aiStatLast30d: 'Aktivität letzte 30 Tage',
    aiAdminStatsTitle: 'Globale KI-Statistiken', aiAdminActiveUsers: 'Aktive Nutzer',
    aiAdminTopUsers: 'Top-Nutzer nach Nachrichten',
  },
  // ========== Compact packs (fall back to English for missing keys) ==========
  fr: {
    appName: 'SanitàNexus', tagline: 'La communauté des professionnels de santé',
    login: 'Se connecter', register: 'S\u2019inscrire', email: 'Email', password: 'Mot de passe',
    fullName: 'Nom complet', specialty: 'Spécialité', bio: 'Bio',
    websiteUrl: 'Site web', linkedinUrl: 'Profil LinkedIn',
    category: 'Catégorie', medici: 'Médecins', infermieri: 'Infirmiers', oss: 'Aide-soignants',
    medico: 'Médecin', infermiere: 'Infirmier', operatoreSS: 'Aide-soignant', logout: 'Déconnexion',
    feed: 'Fil', bacheca: 'Tableau', chat: 'Chat', directory: 'Collègues',
    profile: 'Profil', settings: 'Paramètres', resources: 'Ressources',
    notifications: 'Notifications', admin: 'Admin', wellness: 'Bien-être',
    aiAssistant: 'Assistant IA Clinique', feedbackTitle: 'Retours & Co-conception',
    generale: 'Général', newPost: 'Nouveau post', publish: 'Publier', cancel: 'Annuler',
    save: 'Enregistrer', send: 'Envoyer', search: 'Rechercher', loading: 'Chargement...',
    all: 'Tous', language: 'Langue', theme: 'Thème', light: 'Clair', dark: 'Sombre',
    savedTitle: 'Favoris', shiftsTitle: 'Gardes & rappels',
    dailyBriefingTitle: 'Bonjour ! Votre brief du jour',
    todayHighlightsTitle: 'À la une aujourd\u2019hui',
    trendingPosts: 'Publications tendances', latestInitiatives: 'Dernières initiatives',
    topResources: 'Ressources populaires', upcoming: 'À venir',
    analyticsTitle: 'Analyses par catégorie',
  },
  pt: {
    appName: 'SanitàNexus', tagline: 'A comunidade dos profissionais de saúde',
    login: 'Entrar', register: 'Registar', email: 'Email', password: 'Palavra-passe',
    fullName: 'Nome completo', specialty: 'Especialidade', bio: 'Bio',
    category: 'Categoria', medici: 'Médicos', infermieri: 'Enfermeiros', oss: 'Auxiliares',
    medico: 'Médico', infermiere: 'Enfermeiro', operatoreSS: 'Auxiliar de saúde', logout: 'Sair',
    feed: 'Feed', bacheca: 'Quadro', chat: 'Chat', directory: 'Colegas',
    profile: 'Perfil', settings: 'Definições', resources: 'Recursos',
    notifications: 'Notificações', admin: 'Admin', wellness: 'Bem-estar',
    aiAssistant: 'Assistente IA Clínico', feedbackTitle: 'Feedback & Co-design',
    generale: 'Geral', newPost: 'Novo post', publish: 'Publicar', cancel: 'Cancelar',
    save: 'Guardar', send: 'Enviar', search: 'Pesquisar', loading: 'A carregar...',
    all: 'Todos', language: 'Idioma', theme: 'Tema', light: 'Claro', dark: 'Escuro',
    savedTitle: 'Favoritos', shiftsTitle: 'Turnos & lembretes',
    dailyBriefingTitle: 'Bom dia! O seu briefing de hoje',
    todayHighlightsTitle: 'Em destaque hoje',
    trendingPosts: 'Publicações em destaque', latestInitiatives: 'Últimas iniciativas',
    topResources: 'Recursos populares', upcoming: 'Próximos',
    analyticsTitle: 'Análise por categoria',
  },
  nl: {
    appName: 'SanitàNexus', tagline: 'De zorgprofessionals community',
    login: 'Inloggen', register: 'Registreren', email: 'E-mail', password: 'Wachtwoord',
    fullName: 'Volledige naam', specialty: 'Specialiteit', bio: 'Bio',
    category: 'Categorie', medici: 'Artsen', infermieri: 'Verpleegkundigen', oss: 'Zorgverleners',
    medico: 'Arts', infermiere: 'Verpleegkundige', operatoreSS: 'Zorgverlener', logout: 'Uitloggen',
    feed: 'Feed', bacheca: 'Bord', chat: 'Chat', directory: 'Collega\u2019s',
    profile: 'Profiel', settings: 'Instellingen', resources: 'Bronnen',
    notifications: 'Meldingen', admin: 'Admin', wellness: 'Welzijn',
    aiAssistant: 'Klinische AI-assistent', feedbackTitle: 'Feedback & Co-design',
    generale: 'Algemeen', newPost: 'Nieuw bericht', publish: 'Publiceren', cancel: 'Annuleren',
    save: 'Opslaan', send: 'Verzenden', search: 'Zoeken', loading: 'Laden...',
    all: 'Alle', language: 'Taal', theme: 'Thema', light: 'Licht', dark: 'Donker',
    savedTitle: 'Opgeslagen', shiftsTitle: 'Diensten & herinneringen',
    dailyBriefingTitle: 'Goedemorgen! Uw dagbriefing',
    todayHighlightsTitle: 'Vandaag in de kijker',
    trendingPosts: 'Trending berichten', latestInitiatives: 'Nieuwste initiatieven',
    topResources: 'Top bronnen', upcoming: 'Aankomend',
    analyticsTitle: 'Analyse per categorie',
  },
  pl: {
    appName: 'SanitàNexus', tagline: 'Społeczność pracowników ochrony zdrowia',
    login: 'Zaloguj', register: 'Zarejestruj', email: 'Email', password: 'Hasło',
    fullName: 'Imię i nazwisko', specialty: 'Specjalizacja', bio: 'Bio',
    category: 'Kategoria', medici: 'Lekarze', infermieri: 'Pielęgniarki', oss: 'Opiekunowie',
    medico: 'Lekarz', infermiere: 'Pielęgniarka', operatoreSS: 'Opiekun', logout: 'Wyloguj',
    feed: 'Aktualności', bacheca: 'Tablica', chat: 'Czat', directory: 'Koledzy',
    profile: 'Profil', settings: 'Ustawienia', resources: 'Zasoby',
    notifications: 'Powiadomienia', admin: 'Admin', wellness: 'Dobrostan',
    aiAssistant: 'Kliniczny asystent AI', feedbackTitle: 'Opinie & Co-design',
    generale: 'Ogólne', newPost: 'Nowy post', publish: 'Publikuj', cancel: 'Anuluj',
    save: 'Zapisz', send: 'Wyślij', search: 'Szukaj', loading: 'Ładowanie...',
    all: 'Wszystkie', language: 'Język', theme: 'Motyw', light: 'Jasny', dark: 'Ciemny',
    savedTitle: 'Zapisane', shiftsTitle: 'Dyżury i przypomnienia',
    dailyBriefingTitle: 'Dzień dobry! Twój codzienny brief',
    todayHighlightsTitle: 'Dziś w centrum uwagi',
    trendingPosts: 'Popularne posty', latestInitiatives: 'Najnowsze inicjatywy',
    topResources: 'Popularne zasoby', upcoming: 'Nadchodzące',
    analyticsTitle: 'Analityka według kategorii',
  },
  ro: {
    appName: 'SanitàNexus', tagline: 'Comunitatea profesioniștilor din sănătate',
    login: 'Autentificare', register: 'Înregistrare', email: 'Email', password: 'Parolă',
    fullName: 'Nume complet', specialty: 'Specialitate', bio: 'Bio',
    category: 'Categorie', medici: 'Medici', infermieri: 'Asistenți medicali', oss: 'Îngrijitori',
    medico: 'Medic', infermiere: 'Asistent medical', operatoreSS: 'Îngrijitor', logout: 'Ieșire',
    feed: 'Flux', bacheca: 'Panou', chat: 'Chat', directory: 'Colegi',
    profile: 'Profil', settings: 'Setări', resources: 'Resurse',
    notifications: 'Notificări', admin: 'Admin', wellness: 'Bunăstare',
    aiAssistant: 'Asistent AI Clinic', feedbackTitle: 'Feedback & Co-design',
    generale: 'General', newPost: 'Postare nouă', publish: 'Publică', cancel: 'Anulează',
    save: 'Salvează', send: 'Trimite', search: 'Caută', loading: 'Se încarcă...',
    all: 'Toate', language: 'Limbă', theme: 'Temă', light: 'Luminos', dark: 'Întunecat',
    savedTitle: 'Salvate', shiftsTitle: 'Ture și memento-uri',
    dailyBriefingTitle: 'Bună dimineața! Briefing-ul tău de azi',
    todayHighlightsTitle: 'Astăzi în evidență',
    trendingPosts: 'Postări în trend', latestInitiatives: 'Ultimele inițiative',
    topResources: 'Resurse populare', upcoming: 'Următoare',
    analyticsTitle: 'Analiză pe categorii',
  },
  el: {
    appName: 'SanitàNexus', tagline: 'Η κοινότητα των επαγγελματιών υγείας',
    login: 'Σύνδεση', register: 'Εγγραφή', email: 'Email', password: 'Κωδικός',
    fullName: 'Ονοματεπώνυμο', specialty: 'Ειδικότητα', bio: 'Bio',
    category: 'Κατηγορία', medici: 'Ιατροί', infermieri: 'Νοσηλευτές', oss: 'Βοηθοί υγείας',
    medico: 'Ιατρός', infermiere: 'Νοσηλευτής', operatoreSS: 'Βοηθός υγείας', logout: 'Αποσύνδεση',
    feed: 'Ροή', bacheca: 'Πίνακας', chat: 'Συνομιλία', directory: 'Συνάδελφοι',
    profile: 'Προφίλ', settings: 'Ρυθμίσεις', resources: 'Πόροι',
    notifications: 'Ειδοποιήσεις', admin: 'Διαχείριση', wellness: 'Ευεξία',
    aiAssistant: 'Κλινικός βοηθός AI', feedbackTitle: 'Σχόλια & Co-design',
    generale: 'Γενικά', newPost: 'Νέα ανάρτηση', publish: 'Δημοσίευση', cancel: 'Άκυρο',
    save: 'Αποθήκευση', send: 'Αποστολή', search: 'Αναζήτηση', loading: 'Φόρτωση...',
    all: 'Όλα', language: 'Γλώσσα', theme: 'Θέμα', light: 'Φωτεινό', dark: 'Σκοτεινό',
    savedTitle: 'Αποθηκευμένα', shiftsTitle: 'Βάρδιες & υπενθυμίσεις',
    dailyBriefingTitle: 'Καλημέρα! Το ημερήσιο ενημερωτικό σας',
    todayHighlightsTitle: 'Στο επίκεντρο σήμερα',
    trendingPosts: 'Δημοφιλείς αναρτήσεις', latestInitiatives: 'Πρόσφατες πρωτοβουλίες',
    topResources: 'Δημοφιλείς πόροι', upcoming: 'Προσεχή',
    analyticsTitle: 'Στατιστικά ανά κατηγορία',
  },
  ru: {
    appName: 'SanitàNexus', tagline: 'Сообщество медицинских работников',
    login: 'Войти', register: 'Регистрация', email: 'Email', password: 'Пароль',
    fullName: 'Полное имя', specialty: 'Специализация', bio: 'О себе',
    category: 'Категория', medici: 'Врачи', infermieri: 'Медсёстры', oss: 'Санитары',
    medico: 'Врач', infermiere: 'Медсестра', operatoreSS: 'Санитар', logout: 'Выйти',
    feed: 'Лента', bacheca: 'Доска', chat: 'Чат', directory: 'Коллеги',
    profile: 'Профиль', settings: 'Настройки', resources: 'Ресурсы',
    notifications: 'Уведомления', admin: 'Админ', wellness: 'Благополучие',
    aiAssistant: 'Клинический AI-ассистент', feedbackTitle: 'Отзывы и Co-design',
    generale: 'Общее', newPost: 'Новый пост', publish: 'Опубликовать', cancel: 'Отмена',
    save: 'Сохранить', send: 'Отправить', search: 'Поиск', loading: 'Загрузка...',
    all: 'Все', language: 'Язык', theme: 'Тема', light: 'Светлая', dark: 'Тёмная',
    savedTitle: 'Сохранённое', shiftsTitle: 'Смены и напоминания',
    dailyBriefingTitle: 'Доброе утро! Ваш дневной брифинг',
    todayHighlightsTitle: 'Сегодня в центре внимания',
    trendingPosts: 'Популярные посты', latestInitiatives: 'Новые инициативы',
    topResources: 'Топовые ресурсы', upcoming: 'Предстоящие',
    analyticsTitle: 'Аналитика по категориям',
  },
  ar: {
    appName: 'SanitàNexus', tagline: 'مجتمع المتخصصين في الرعاية الصحية',
    login: 'تسجيل الدخول', register: 'إنشاء حساب', email: 'البريد الإلكتروني', password: 'كلمة المرور',
    fullName: 'الاسم الكامل', specialty: 'التخصص', bio: 'نبذة',
    category: 'الفئة', medici: 'أطباء', infermieri: 'ممرضون', oss: 'مساعدو رعاية',
    medico: 'طبيب', infermiere: 'ممرض', operatoreSS: 'مساعد رعاية', logout: 'خروج',
    feed: 'التدفق', bacheca: 'اللوحة', chat: 'محادثة', directory: 'الزملاء',
    profile: 'الملف الشخصي', settings: 'الإعدادات', resources: 'الموارد',
    notifications: 'الإشعارات', admin: 'المشرف', wellness: 'الرفاهية',
    aiAssistant: 'مساعد الذكاء الاصطناعي السريري', feedbackTitle: 'التعليقات والتصميم المشترك',
    generale: 'عام', newPost: 'منشور جديد', publish: 'نشر', cancel: 'إلغاء',
    save: 'حفظ', send: 'إرسال', search: 'بحث', loading: 'جار التحميل...',
    all: 'الكل', language: 'اللغة', theme: 'النمط', light: 'فاتح', dark: 'داكن',
    savedTitle: 'المحفوظات', shiftsTitle: 'المناوبات والتذكيرات',
    dailyBriefingTitle: 'صباح الخير! ملخصك اليومي',
    todayHighlightsTitle: 'أبرز أحداث اليوم',
    trendingPosts: 'المنشورات الرائجة', latestInitiatives: 'أحدث المبادرات',
    topResources: 'أفضل الموارد', upcoming: 'القادمة',
    analyticsTitle: 'التحليلات حسب الفئة',
  },
  zh: {
    appName: 'SanitàNexus', tagline: '医疗专业人员社区',
    login: '登录', register: '注册', email: '邮箱', password: '密码',
    fullName: '姓名', specialty: '专业', bio: '简介',
    category: '类别', medici: '医生', infermieri: '护士', oss: '护理员',
    medico: '医生', infermiere: '护士', operatoreSS: '护理员', logout: '退出',
    feed: '动态', bacheca: '公告板', chat: '聊天', directory: '同事',
    profile: '个人资料', settings: '设置', resources: '资源',
    notifications: '通知', admin: '管理', wellness: '健康',
    aiAssistant: '临床AI助手', feedbackTitle: '反馈与共同设计',
    generale: '通用', newPost: '新帖子', publish: '发布', cancel: '取消',
    save: '保存', send: '发送', search: '搜索', loading: '加载中...',
    all: '全部', language: '语言', theme: '主题', light: '浅色', dark: '深色',
    savedTitle: '已保存', shiftsTitle: '排班与提醒',
    dailyBriefingTitle: '早上好！您的每日简报',
    todayHighlightsTitle: '今日焦点',
    trendingPosts: '热门帖子', latestInitiatives: '最新倡议',
    topResources: '热门资源', upcoming: '即将到来',
    analyticsTitle: '按类别分析',
  },
  ja: {
    appName: 'SanitàNexus', tagline: '医療専門家のコミュニティ',
    login: 'ログイン', register: '登録', email: 'メール', password: 'パスワード',
    fullName: '氏名', specialty: '専門分野', bio: '自己紹介',
    category: 'カテゴリ', medici: '医師', infermieri: '看護師', oss: '介護士',
    medico: '医師', infermiere: '看護師', operatoreSS: '介護士', logout: 'ログアウト',
    feed: 'フィード', bacheca: '掲示板', chat: 'チャット', directory: '同僚',
    profile: 'プロフィール', settings: '設定', resources: 'リソース',
    notifications: '通知', admin: '管理', wellness: 'ウェルネス',
    aiAssistant: '臨床AIアシスタント', feedbackTitle: 'フィードバック & 共同設計',
    generale: '一般', newPost: '新規投稿', publish: '公開', cancel: 'キャンセル',
    save: '保存', send: '送信', search: '検索', loading: '読み込み中...',
    all: 'すべて', language: '言語', theme: 'テーマ', light: 'ライト', dark: 'ダーク',
    savedTitle: '保存済み', shiftsTitle: 'シフトとリマインダー',
    dailyBriefingTitle: 'おはようございます！本日のブリーフィング',
    todayHighlightsTitle: '今日のハイライト',
    trendingPosts: 'トレンド投稿', latestInitiatives: '最新のイニシアチブ',
    topResources: '人気のリソース', upcoming: '今後の予定',
    analyticsTitle: 'カテゴリ別分析',
  },
};

const resources = {
  it: { translation: common.it },
  en: { translation: common.en },
  es: { translation: common.es },
  de: { translation: common.de },
  fr: { translation: common.fr },
  pt: { translation: common.pt },
  nl: { translation: common.nl },
  pl: { translation: common.pl },
  ro: { translation: common.ro },
  el: { translation: common.el },
  ru: { translation: common.ru },
  ar: { translation: common.ar },
  zh: { translation: common.zh },
  ja: { translation: common.ja },
};

export const AVAILABLE_LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
];

export const RTL_LANGUAGES = ['ar'];

const savedLng = typeof window !== 'undefined' ? (localStorage.getItem('sc_lang') || 'it') : 'it';

// Apply RTL direction if needed
if (typeof document !== 'undefined') {
  document.documentElement.dir = RTL_LANGUAGES.includes(savedLng) ? 'rtl' : 'ltr';
  document.documentElement.lang = savedLng;
}

i18n.use(initReactI18next).init({
  resources, lng: savedLng, fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  }
});

export default i18n;
